import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Nav, Form, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import PlusIcon from '../../assets/images/admin-icons/Puls-icon.png';
import { getTournamentById, ManageTournament, formatDate, getTournamentLogoUrl, getAllTournamentSeasons, TournamentSeason, addTournamentSeason, editTournamentSeason, deleteTournamentSeason, deleteTournament } from '../../api/tournament/manageTournamentService';
import { editTournament, TournamentPayload } from '../../api/tournament/tournamentService';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import TournamentHeader from '../../Common/TournamentHeader';
// Import tab components
import AgeCategoryManagement from '../../Common/AgeCategoryManagement';
import TeamsTable from '../TournamentDetails/components/TeamsTable';
import PlayersTable from '../TournamentDetails/components/PlayersTable';
import MatchManagementTable from '../TournamentDetails/components/MatchManagementTable';
import FixturesTable from '../TournamentDetails/components/FixturesTable';
import VotingManagement from '../TournamentDetails/components/VotingManagement';
import PredictionsManagement from '../TournamentDetails/components/PredictionsManagement';
// Import API services for tabs
import { getAllAgeCategoriesPaginated } from '../../api/tournament/ageCategoryService';
import { listTeams, addTeam, editTeam, deleteTeam } from '../../api/tournament/teamService';
import { listPlayers, addPlayer, editPlayer, deletePlayer } from '../../api/tournament/playerService';
import { listFixtures, addFixture, editFixture, deleteFixture, downloadFixturesExcelWithFilters, getCalendarEvents, CalendarEvent } from '../../api/tournament/fixturesService';
import MatchCalendar from '../../Common/MatchCalendar';
import { isSuperAdmin, isOperationsAdmin } from '../../utils/roleBasedAccess';
import LogoUpload from '../../components/LogoUpload/LogoUpload';
import { useSelector } from 'react-redux';
import { getSportValue } from '../../utils/getSportValue';
import { hasAnyPermission, hasTournamentWriteAccess } from '../../utils/roleBasedAccess';
import '../../pages/ManageTournaments/ManageTournaments.css';
import VideosTab from './Videos/VideosTab';
import TournamentContentTab from './Content/TournamentContentTab';
import {
  listTournamentFeatureToggles,
  setTournamentFeatureToggle,
  TournamentFeatureToggle,
} from '../../api/tournament/featureToggleService';
import { getSportAdapter } from '../../api/tournament/sportAdapter';
import {
  buildTvPassConfig,
  SEASON_LOCATION_ERROR_MESSAGE,
  SEASON_LOCATION_INPUT_PATTERN,
  SEASON_LOCATION_PATTERN,
  setFeatureToggleEnabled,
} from './seasonSetup';

type TabType = 'details' | 'age-categories' | 'teams' | 'players' | 'player-stats' | 'match-management' | 'standings' | 'videos' | 'settings' | 'content' | 'voting' | 'predictions';

const TournamentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);

  // Extract defaultSeasonId from location state (for single-season tournaments)
  const defaultSeasonIdFromState = (location.state as any)?.defaultSeasonId;
  // Extract selectedTournament from location state (contains sports info)
  const selectedTournamentFromState = (location.state as any)?.selectedTournament;

  // Season state - will be set from location.state or fetched if not available
  const [defaultSeasonId, setDefaultSeasonId] = useState<string | undefined>(defaultSeasonIdFromState);

  // Tournament state
  const [tournament, setTournament] = useState<ManageTournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('details');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<TournamentPayload>({
    name: '',
    shortName: '',
    logo: '',
    location: '',
    startDate: '',
    endDate: '',
    status: '',
    sports: '',
    sortNo: 1
  });
  const [editLoading, setEditLoading] = useState(false);
  const [isDeletingTournament, setIsDeletingTournament] = useState(false);
  const [featureToggles, setFeatureToggles] = useState<TournamentFeatureToggle[]>([]);
  const [featureToggleError, setFeatureToggleError] = useState<string | null>(null);
  const [featureTogglesLoading, setFeatureTogglesLoading] = useState(false);
  const [savingFeatureToggles, setSavingFeatureToggles] = useState<string[]>([]);

  // Inline editing state
  const [isEditing, setIsEditing] = useState(false);
  const [inlineEditData, setInlineEditData] = useState<TournamentPayload>({
    name: '',
    shortName: '',
    logo: '',
    location: '',
    startDate: '',
    endDate: '',
    status: '',
    sports: '',
    sortNo: 1
  });

  // Validation and error state
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Tab functionality state
  const [ageCategories, setAgeCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState<any[]>([]);
  const [matchStatusFilter, setMatchStatusFilter] = useState<{ value: string; label: string } | null>(null);
  const [initialOpenFixture, setInitialOpenFixture] = useState<{ id: string; mode: 'view' | 'edit' } | null>(null);

  // Age category tab state
  const [ageCategorySearch, setAgeCategorySearch] = useState('');
  const [ageCategoriesLoading, setAgeCategoriesLoading] = useState(false);
  const [ageCategoriesError, setAgeCategoriesError] = useState<string | null>(null);
  const [ageCategoryPage, setAgeCategoryPage] = useState(1);
  const [ageCategoryPageSize, setAgeCategoryPageSize] = useState(10);
  const [ageCategoryTotalPages, setAgeCategoryTotalPages] = useState(1);
  const [ageCategoryTotalCount, setAgeCategoryTotalCount] = useState(0);
  const [ageCategoryRefresh, setAgeCategoryRefresh] = useState(false);

  // Teams tab state
  const [teamSearch, setTeamSearch] = useState('');
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [teamsPage, setTeamsPage] = useState(1);
  const [teamsPageSize, setTeamsPageSize] = useState(10);
  const [teamsTotalPages, setTeamsTotalPages] = useState(1);
  const [teamsTotalCount, setTeamsTotalCount] = useState(0);
  const [teamModalRefresh, setTeamModalRefresh] = useState(false);
  const [teamsAgeCategoryFilter, setTeamsAgeCategoryFilter] = useState<any>(null);

  // Players tab state
  const [playerSearch, setPlayerSearch] = useState('');
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState<string | null>(null);
  const [playersPage, setPlayersPage] = useState(1);
  const [playersPageSize, setPlayersPageSize] = useState(10);
  const [playersTotalPages, setPlayersTotalPages] = useState(1);
  const [playersTotalCount, setPlayersTotalCount] = useState(0);
  const [playersRefresh, setPlayersRefresh] = useState(false);

  // Player Stats tab state
  const [playerStatsSearch, setPlayerStatsSearch] = useState('');
  const [playerStatsLoading, setPlayerStatsLoading] = useState(false);
  const [playerStatsError, setPlayerStatsError] = useState<string | null>(null);
  const [playerStatsPage, setPlayerStatsPage] = useState(1);
  const [playerStatsPageSize, setPlayerStatsPageSize] = useState(10);
  const [playerStatsTotalPages, setPlayerStatsTotalPages] = useState(1);
  const [playerStatsTotalCount, setPlayerStatsTotalCount] = useState(0);
  const [playerStatsRefresh, setPlayerStatsRefresh] = useState(false);
  const [playerStats, setPlayerStats] = useState<any[]>([]);
  const [playerStatsAgeCategoryFilter, setPlayerStatsAgeCategoryFilter] = useState<any>(null);

  // Fixtures tab state
  const [fixtureSearch, setFixtureSearch] = useState('');
  const [fixturesAgeCategoryFilter, setFixturesAgeCategoryFilter] = useState<{ value: string; label: string } | null>(null);
  const [fixtures, setFixtures] = useState<any[]>([]);
  const [fixturesLoading, setFixturesLoading] = useState(false);
  const [fixturesError, setFixturesError] = useState<string | null>(null);
  const [fixturesPage, setFixturesPage] = useState(1);
  const [fixturesPageSize, setFixturesPageSize] = useState(50);
  const [fixturesTotalPages, setFixturesTotalPages] = useState(1);
  const [fixturesTotalCount, setFixturesTotalCount] = useState(0);
  const [fixturesRefresh, setFixturesRefresh] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  // Download Excel modal state
  const [showDownloadExcelModal, setShowDownloadExcelModal] = useState(false);
  const [excelDownloadStartDate, setExcelDownloadStartDate] = useState<Date | null>(null);
  const [excelDownloadEndDate, setExcelDownloadEndDate] = useState<Date | null>(null);
  const [excelDownloadStatus, setExcelDownloadStatus] = useState<'pending' | 'completed' | ''>('');
  const [excelDownloadError, setExcelDownloadError] = useState<string | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loadingCalendarEvents, setLoadingCalendarEvents] = useState(false);

  // Standings tab state
  const [standingsSearch, setStandingsSearch] = useState('');
  const [standingsAgeCategoryFilter, setStandingsAgeCategoryFilter] = useState<{ value: string; label: string } | null>(null);
  const [standingsGroupFilter, setStandingsGroupFilter] = useState<{ value: string; label: string } | null>(null);
  const [standings, setStandings] = useState<any[]>([]);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [standingsError, setStandingsError] = useState<string | null>(null);
  const [standingsPage, setStandingsPage] = useState(1);
  const [standingsPageSize, setStandingsPageSize] = useState(10);
  const [standingsTotalPages, setStandingsTotalPages] = useState(1);
  const [standingsTotalCount, setStandingsTotalCount] = useState(0);
  const [standingsRefresh, setStandingsRefresh] = useState(false);

  // Team/age category options for player modal
  const [teamOptions, setTeamOptions] = useState<{ value: string; label: string }[]>([]);
  const [ageCategoryOptionsForPlayers, setAgeCategoryOptionsForPlayers] = useState<{ value: string; label: string }[]>([]);
  const [playersTeamFilter, setPlayersTeamFilter] = useState<any>(null);
  const [playersAgeCategoryFilter, setPlayersAgeCategoryFilter] = useState<any>(null);

  // Season management state
  const [seasons, setSeasons] = useState<TournamentSeason[]>([]);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [isEditingSeason, setIsEditingSeason] = useState(false);
  const [editingSeason, setEditingSeason] = useState<TournamentSeason | null>(null);
  const [hasDefaultSeason, setHasDefaultSeason] = useState(false);
  const [seasonFormData, setSeasonFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    location: '',
    seasonYearRange: '',
    isFeaturedOnTv: false,
    showOnHomePage: true,
    tvSortOrder: '2',
    tvPreviewDuration: '',
    sortOrder: '2',
    tvAccessType: 'paid',
    recordingAccessType: 'free',
    highlightAccessType: 'free',
    matchPassEnabled: false,
    matchPassPrice: '',
    seasonPassEnabled: false,
    seasonPassPrice: '',
    tournamentPassEnabled: false,
  });
  const [seasonFormErrors, setSeasonFormErrors] = useState<{ [key: string]: string }>({});
  const [seasonFormLoading, setSeasonFormLoading] = useState(false);

  // Season flow state - track expanded seasons and active tabs per season
  const [expandedSeasons, setExpandedSeasons] = useState<Set<string>>(new Set());
  const [seasonActiveTabs, setSeasonActiveTabs] = useState<{ [seasonId: string]: TabType }>({});

  // Filter state for age categories
  const [typeFilter, setTypeFilter] = useState(null);
  const [formatFilter, setFormatFilter] = useState(null);
  // Dynamic options extracted from age categories data
  // Always include default options: "Group" and "Single"
  const typeOptions = React.useMemo(() => {
    // Default options that should always be available
    const defaultOptions = [
      { value: 'group', label: 'Group' },
      { value: 'single', label: 'Single' }
    ];

    // Use standings data when in standings tab, otherwise use ageCategories
    const dataSource = activeTab === 'standings' ? standings : ageCategories;

    if (!dataSource || dataSource.length === 0) {
      // If no data, return only default options
      return defaultOptions;
    }

    // Extract unique standings types from existing data
    const uniqueTypes = Array.from(new Set(
      dataSource
        .map((ac: any) => ac.standingsType)
        .filter((type: any) => type && type.trim() !== '')
    )).sort();

    // Map to options format
    const dynamicOptions = uniqueTypes.map((type: string) => ({
      value: type.toLowerCase(),
      label: type.charAt(0).toUpperCase() + type.slice(1)
    }));

    // Merge default options with dynamic options, avoiding duplicates
    const allOptions = [...defaultOptions];
    dynamicOptions.forEach(opt => {
      if (!allOptions.find(o => o.value === opt.value)) {
        allOptions.push(opt);
      }
    });

    return allOptions;
  }, [ageCategories, standings, activeTab]);

  const formatOptions = React.useMemo(() => {
    return [
      { value: 'team', label: 'Team' },
      { value: 'player', label: 'Player' }
    ];
  }, []);

  // Check if user has access
  const userData = {
    ...user,
    subRole: role,
    permissions: permissions,
  };

  const canAccessFixtures = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.tournaments.view',
    'tournament.tournaments.manage',
    'tournament.fixtures.manage',
    'tournament.view',
    'tournament.read',
  ]);
  const canManageContent = hasTournamentWriteAccess(userData, [
    'tournament.tournaments.manage',
    'tournament.fixtures.manage',
  ]);
  const sportAdapter = getSportAdapter(tournament?.sports || '');
  const supports = (feature: 'fixtures' | 'playerStats' | 'standings' | 'content') =>
    sportAdapter.features.includes(feature);

  useEffect(() => {
    if (!defaultSeasonId) {
      setFeatureToggles([]);
      return;
    }

    setFeatureTogglesLoading(true);
    listTournamentFeatureToggles(defaultSeasonId)
      .then((toggles) => {
        setFeatureToggles(toggles);
        setFeatureToggleError(null);
      })
      .catch(() => {
        setFeatureToggles([]);
        setFeatureToggleError('Tournament settings are temporarily unavailable.');
      })
      .finally(() => {
        setFeatureTogglesLoading(false);
      });
  }, [defaultSeasonId]);

  const updateFeatureToggle = async (toggle: TournamentFeatureToggle, enabled: boolean) => {
    if (!defaultSeasonId || savingFeatureToggles.includes(toggle.name)) {
      return;
    }

    const previousEnabled = toggle.enabled;
    setFeatureToggleError(null);
    setSavingFeatureToggles((current) => [...current, toggle.name]);
    setFeatureToggles((current) => setFeatureToggleEnabled(current, toggle.name, enabled));

    try {
      setFeatureToggles(await setTournamentFeatureToggle(defaultSeasonId, toggle.name, enabled));
    } catch (err: any) {
      setFeatureToggles((current) => setFeatureToggleEnabled(current, toggle.name, previousEnabled));
      setFeatureToggleError(err?.message || 'Unable to update tournament setting.');
    } finally {
      setSavingFeatureToggles((current) => current.filter((name) => name !== toggle.name));
    }
  };

  // URL parameter handling for match-management tab
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL parameters when match-management tab becomes active
  useEffect(() => {
    if (activeTab === 'match-management') {
      const pageSizeParam = searchParams.get('pageSize');
      const pageNumParam = searchParams.get('pageNum');

      if (pageSizeParam) {
        const pageSize = parseInt(pageSizeParam, 10);
        if (!isNaN(pageSize) && pageSize > 0 && pageSize !== fixturesPageSize) {
          setFixturesPageSize(pageSize);
        }
      }

      if (pageNumParam) {
        const pageNum = parseInt(pageNumParam, 10);
        if (!isNaN(pageNum) && pageNum > 0 && pageNum !== fixturesPage) {
          setFixturesPage(pageNum);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Only run when tab changes to match-management

  // Update URL parameters when fixturesPage or fixturesPageSize changes (only for match-management tab)
  useEffect(() => {
    if (activeTab === 'match-management') {
      const params = new URLSearchParams(searchParams);
      let shouldUpdate = false;

      // Always update pageSize in URL
      const currentPageSize = params.get('pageSize');
      const expectedPageSize = fixturesPageSize.toString();
      if (currentPageSize !== expectedPageSize) {
        params.set('pageSize', fixturesPageSize.toString());
        shouldUpdate = true;
      }

      // Always update pageNum in URL (always pass it, just like pageSize)
      const currentPageNum = params.get('pageNum');
      const expectedPageNum = fixturesPage.toString();
      if (currentPageNum !== expectedPageNum) {
        params.set('pageNum', fixturesPage.toString());
        shouldUpdate = true;
      }

      // Only update URL if params actually changed
      if (shouldUpdate) {
        setSearchParams(params, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixturesPage, fixturesPageSize, activeTab]);

  // Load tournament details
  useEffect(() => {
    if (id && canAccessFixtures) {
      loadTournamentDetails();
    }
  }, [id, canAccessFixtures]);

  // Handle activeTab from navigation state
  useEffect(() => {
    // Only set activeTab from navigation state if it's explicitly provided and valid
    if (location.state?.activeTab &&
      typeof location.state.activeTab === 'string' &&
      ['details', 'age-categories', 'teams', 'players', 'player-stats', 'match-management', 'standings', 'videos', 'content'].includes(location.state.activeTab)) {
      setActiveTab(location.state.activeTab as TabType);
    }
  }, [location.state]);

  // Helper function to get sport value safely
  // Uses shared utility to ensure correct sport is always used
  const getSport = useCallback((): string => {
    return getSportValue(tournament, location);
  }, [tournament, location]);

  // Helper function to check if sport is Badminton or Table Tennis
  const isBadmintonOrTT = useCallback((): boolean => {
    try {
      const sport = getSport().toLowerCase();
      return sport === 'badminton' || sport === 'table tennis' || sport === 'tabletennis' || sport === 'tt';
    } catch {
      return false;
    }
  }, [getSport]);

  // Helper function to check if sport is specifically Badminton
  const isBadminton = useCallback((): boolean => {
    try {
      const sport = getSport().toLowerCase();
      return sport === 'badminton';
    } catch {
      return false;
    }
  }, [getSport]);

  // Redirect away from player-stats tab if sport is badminton
  useEffect(() => {
    if (activeTab === 'player-stats' && isBadminton()) {
      setActiveTab('details');
    }
  }, [activeTab, isBadminton]);

  // Redirect nested season tabs away from player-stats if sport is badminton
  useEffect(() => {
    if (!isBadminton()) return;

    setSeasonActiveTabs(prev => {
      const hasPlayerStatsTab = Object.values(prev).some(tab => tab === 'player-stats');
      if (!hasPlayerStatsTab) return prev;

      const updated: { [seasonId: string]: TabType } = {};
      Object.keys(prev).forEach(seasonId => {
        updated[seasonId] = prev[seasonId] === 'player-stats' ? 'age-categories' : prev[seasonId];
      });
      return updated;
    });
  }, [isBadminton]);

  // Note: Standings tab is now available for Badminton/TT to show knockouts

  // Load age categories when Age Category tab is active
  useEffect(() => {
    if (activeTab === 'age-categories' && tournament?._id) {
      setAgeCategoriesLoading(true);
      setAgeCategoriesError(null);
      try {
        // Always pass defaultSeasonId when available (for single-season tournaments)
        const sport = getSport();
        getAllAgeCategoriesPaginated(tournament._id, ageCategoryPage, ageCategoryPageSize, ageCategorySearch, (typeFilter as any)?.value || '', (formatFilter as any)?.value || '', defaultSeasonId || undefined, sport)
          .then(data => {
            const arr = data?.paginatedResults || [];
            const mapped = arr.map((ac: any) => ({
              ...ac,
              format: ac.format || '-',
              tournament: ac.tournament?.name || ac.tournament || '',
            }));
            setAgeCategories(mapped);
            setAgeCategoryTotalPages(data?.totalPages || 1);
            setAgeCategoryTotalCount(data?.totalCount || 0);
            setAgeCategoriesLoading(false);
          })
          .catch((err) => {
            console.error('Error loading age categories:', err);
            setAgeCategoriesError('Failed to load age categories.');
            setAgeCategoriesLoading(false);
          });
      } catch (error) {
        console.error('Error getting sport for age categories:', error);
        setAgeCategoriesError('Failed to determine sport.');
        setAgeCategoriesLoading(false);
      }
    }
  }, [activeTab, ageCategoryPage, ageCategoryPageSize, ageCategorySearch, typeFilter, formatFilter, tournament?._id, ageCategoryRefresh, defaultSeasonId, getSport]);

  // Load teams when Teams tab is active
  useEffect(() => {
    if (activeTab === 'teams' && tournament?._id) {
      setTeamsLoading(true);
      setTeamsError(null);
      const ageCategory = teamsAgeCategoryFilter && teamsAgeCategoryFilter.length > 0
        ? teamsAgeCategoryFilter.map((filter: any) => filter.value).join(',')
        : '';
      // Always pass defaultSeasonId when available (for single-season tournaments)
      listTeams({
        page: teamsPage,
        pageSize: teamsPageSize,
        tournamentId: tournament._id,
        search: teamSearch,
        ageCategory,
        tournamentSeason: defaultSeasonId || undefined,
        sport: getSport() // FIX: Pass sport parameter - use safe getter
      })
        .then(data => {
          const arr = data?.paginatedResults || [];
          setTeams(arr);
          setTeamsTotalPages(data?.totalPages || 1);
          setTeamsTotalCount(data?.totalCount || 0);
          setTeamsLoading(false);
        })
        .catch(() => {
          setTeamsError('Failed to load teams.');
          setTeamsLoading(false);
        });
    }
  }, [activeTab, teamModalRefresh, teamsPage, teamsPageSize, teamSearch, teamsAgeCategoryFilter, tournament?._id, defaultSeasonId]);

  // Load players when Players tab is active
  useEffect(() => {
    if (activeTab === 'players' && tournament?._id) {
      console.log('🔄 TournamentDetailPage - Loading players:', {
        activeTab,
        tournamentId: tournament._id,
        defaultSeasonId,
        page: playersPage,
        pageSize: playersPageSize,
        search: playerSearch,
        ageCategory: playersAgeCategoryFilter?.label
      });
      setPlayersLoading(true);
      setPlayersError(null);
      listPlayers({
        page: playersPage,
        pageSize: playersPageSize,
        tournament: tournament._id,
        ageCategory: playersAgeCategoryFilter?.label || '',
        search: playerSearch,
        tournamentSeason: defaultSeasonId || undefined, // Always pass defaultSeasonId when available (for single-season tournaments)
        sport: getSport() // FIX: Pass sport parameter - use safe getter
      })
        .then(data => {
          const arr = data?.paginatedResults || [];
          setPlayers(arr);
          setPlayersTotalPages(data?.totalPages || 1);
          setPlayersTotalCount(data?.totalCount || 0);
          setPlayersLoading(false);
        })
        .catch((error) => {
          console.error('❌ TournamentDetailPage - Error loading players:', error);
          setPlayersError('Failed to load players.');
          setPlayersLoading(false);
        });
    } else {
      console.log('⏭️ TournamentDetailPage - Skipping players load:', {
        activeTab,
        hasTournament: !!tournament?._id
      });
    }
  }, [activeTab, playersPage, playersPageSize, playerSearch, playersAgeCategoryFilter, playersRefresh, tournament?._id, defaultSeasonId]);

  // Load player stats when Player Stats tab is active
  useEffect(() => {
    if (activeTab === 'player-stats' && tournament?._id) {
      console.log('🔄 TournamentDetailPage - Loading players for player-stats:', {
        activeTab,
        tournamentId: tournament._id,
        defaultSeasonId,
        page: playerStatsPage,
        pageSize: playerStatsPageSize,
        search: playerStatsSearch,
        ageCategory: playerStatsAgeCategoryFilter?.label
      });
      setPlayerStatsLoading(true);
      setPlayerStatsError(null);
      listPlayers({
        page: playerStatsPage,
        pageSize: playerStatsPageSize,
        tournament: tournament._id,
        ageCategory: playerStatsAgeCategoryFilter?.label || '',
        search: playerStatsSearch,
        tournamentSeason: defaultSeasonId || undefined, // Always pass defaultSeasonId when available (for single-season tournaments)
        sport: getSport() // FIX: Pass sport parameter - use safe getter
      })
        .then(data => {
          const raw = data?.paginatedResults || [];
          // Display all players regardless of stats
          setPlayerStats(raw);
          // Keep API totals as-is to preserve pagination behavior
          setPlayerStatsTotalPages(data?.totalPages || 1);
          setPlayerStatsTotalCount(data?.totalCount || 0);
          setPlayerStatsLoading(false);
        })
        .catch((error) => {
          console.error('❌ TournamentDetailPage - Error loading players for player-stats:', error);
          setPlayerStatsError('Failed to load player stats.');
          setPlayerStatsLoading(false);
        });
    } else if (activeTab === 'player-stats') {
      console.log('⏭️ TournamentDetailPage - Skipping player-stats load:', {
        activeTab,
        hasTournament: !!tournament?._id
      });
    }
  }, [activeTab, playerStatsPage, playerStatsPageSize, playerStatsSearch, playerStatsAgeCategoryFilter, playerStatsRefresh, tournament?._id, defaultSeasonId]);

  // Load fixtures when Match Management tab is active
  useEffect(() => {
    if (activeTab === 'match-management' && tournament?._id) {
      setFixturesLoading(true);
      setFixturesError(null);
      listFixtures({
        page: fixturesPage,
        pageSize: fixturesPageSize,
        tournamentId: tournament._id,
        search: fixtureSearch,
        ageCategory: fixturesAgeCategoryFilter?.value || '', // Use value (ObjectId/_id), not label (name)
        status: matchStatusFilter?.value || '',
        tournamentSeason: defaultSeasonId || undefined, // Always pass defaultSeasonId when available (for single-season tournaments)
        sport: getSport() // FIX: Pass sport parameter - use safe getter (singular) - REQUIRED by API
      })
        .then(data => {
          const arr = data?.paginatedResults || [];
          setFixtures(arr);
          setFixturesTotalPages(data?.totalPages || 1);
          setFixturesTotalCount(data?.totalCount || 0);
          setFixturesLoading(false);
        })
        .catch(() => {
          setFixturesError('Failed to load fixtures.');
          setFixturesLoading(false);
        });
    }
  }, [activeTab, fixturesPage, fixturesPageSize, fixtureSearch, fixturesAgeCategoryFilter, matchStatusFilter, fixturesRefresh, tournament?._id, defaultSeasonId]);

  // Reset standings page when switching to standings tab
  useEffect(() => {
    if (activeTab === 'standings') {
      setStandingsPage(1);
    }
  }, [activeTab]);

  // Load age categories for Standings tab (using age category API instead of standings API)
  useEffect(() => {
    if (activeTab === 'standings' && tournament?._id) {
      setStandingsLoading(true);
      setStandingsError(null);
      try {
        // Always pass defaultSeasonId when available (for single-season tournaments)
        const sport = getSport();
        console.log('Loading age categories for standings tab:', { tournamentId: tournament._id, sport, defaultSeasonId, page: standingsPage });
        getAllAgeCategoriesPaginated(tournament._id, standingsPage, standingsPageSize, standingsSearch, (typeFilter as any)?.value || '', (formatFilter as any)?.value || '', defaultSeasonId || undefined, sport)
          .then(data => {
            console.log('Age categories loaded for standings:', data);
            const arr = data?.paginatedResults || [];
            // Map the data similar to age categories tab to ensure consistent structure
            const mapped = arr.map((ac: any) => ({
              ...ac,
              format: ac.format || '-',
              tournament: ac.tournament?.name || ac.tournament || '',
            }));
            setStandings(mapped);
            setStandingsTotalPages(data?.totalPages || 1);
            setStandingsTotalCount(data?.totalCount || 0);
            setStandingsLoading(false);
          })
          .catch((err) => {
            console.error('Error loading age categories for standings:', err);
            setStandingsError('Failed to load age categories.');
            setStandingsLoading(false);
          });
      } catch (error) {
        console.error('Error getting sport for standings:', error);
        setStandingsError('Failed to determine sport.');
        setStandingsLoading(false);
      }
    } else if (activeTab !== 'standings') {
      // Clear standings data when switching away from standings tab
      setStandings([]);
    }
  }, [activeTab, standingsPage, standingsPageSize, standingsSearch, typeFilter, formatFilter, standingsAgeCategoryFilter, standingsGroupFilter, standingsRefresh, tournament?._id, defaultSeasonId, getSport]);

  // Load team options for player modal
  useEffect(() => {
    if ((activeTab === 'players' || activeTab === 'player-stats') && tournament?._id) {
      // Always pass defaultSeasonId when available (for single-season tournaments)
      listTeams({
        page: 1,
        pageSize: 100,
        tournamentId: tournament._id,
        tournamentSeason: defaultSeasonId || undefined,
        sport: getSport() // FIX: Pass sport parameter - use safe getter
      })
        .then(data => {
          const teams = data?.paginatedResults || [];
          const options = teams.map((team: any) => ({ value: team._id, label: team.name }));
          setTeamOptions(options);
        })
        .catch(() => {
          setTeamOptions([]);
        });
    }
  }, [activeTab, tournament?._id, defaultSeasonId]);

  // Load age category options for teams, players, player stats, and fixtures
  useEffect(() => {
    if ((activeTab === 'teams' || activeTab === 'players' || activeTab === 'player-stats' || activeTab === 'match-management') && tournament?._id) {
      // Use the age category listing API to get age categories for filters and forms
      // Always pass defaultSeasonId when available (for single-season tournaments)
      // No format filter applied here so all age categories are available
      getAllAgeCategoriesPaginated(tournament._id, 1, 100, '', '', '', defaultSeasonId || undefined, getSport())
        .then(data => {
          const ageCategoriesData = data?.paginatedResults || [];

          // For teams tab, populate the main ageCategories state
          if (activeTab === 'teams') {
            const mapped = ageCategoriesData.map((ac: any) => ({
              ...ac,
              format: ac.format || '-',
              tournament: ac.tournament?.name || ac.tournament || '',
            }));
            setAgeCategories(mapped);
          }

          // For players, player stats, and match management tabs, populate the options
          if (activeTab === 'players' || activeTab === 'player-stats' || activeTab === 'match-management') {
            const options = ageCategoriesData.map((ac: any) => ({
              value: ac._id,
              label: ac.ageCategory,
              format: ac.format, // Include format to check if it's 'player' or 'Team'
              standingsType: ac.standingsType // Include standingsType for fixtures
            }));
            setAgeCategoryOptionsForPlayers(options);
          }
        })
        .catch(() => {
          if (activeTab === 'teams') {
            setAgeCategories([]);
          } else {
            setAgeCategoryOptionsForPlayers([]);
          }
        });
    }
  }, [activeTab, tournament?._id, defaultSeasonId, getSport]);

  const loadTournamentDetails = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      // Get sport from location state (selectedTournament) or URL search params
      // Sport is REQUIRED for tournament management module
      const sportFromState = selectedTournamentFromState?.sports;
      const urlParams = new URLSearchParams(location.search);
      const sportFromUrl = urlParams.get('sport');
      const sport = sportFromState || sportFromUrl;

      if (!sport) {
        throw new Error('Sport parameter is required. Please navigate from tournament list or include ?sport=Football in URL.');
      }

      const tournamentData = await getTournamentById(id, sport);
      setTournament(tournamentData);

      // If defaultSeasonId is not in location.state, fetch it from API
      // All tournaments should have a season (either DEFAULT or SEASON type)
      if (!defaultSeasonIdFromState && !defaultSeasonId) {
        try {
          // Use the sport we just used to load tournament, or get it from tournament if loaded
          const seasonSport = tournament?.sports || sport;
          const allSeasons = await getAllTournamentSeasons(id, seasonSport);
          // Find DEFAULT season (single-season tournaments) or first SEASON type season
          const defaultSeason = allSeasons.find((s: any) => s.seasonType === 'DEFAULT');
          const firstSeason = allSeasons.find((s: any) => s.seasonType === 'SEASON');

          // Use DEFAULT season if available, otherwise use first SEASON type season
          // If tournament has multiple seasons, user should be on SeasonDetailPage, not TournamentDetailPage
          // But we still need a season ID for API calls
          if (defaultSeason) {
            setDefaultSeasonId(defaultSeason._id);
          } else if (firstSeason) {
            // Fallback: use first SEASON type season (shouldn't happen in normal flow)
            setDefaultSeasonId(firstSeason._id);
          }
        } catch (seasonError: any) {
          console.warn('Could not fetch tournament seasons:', seasonError);
          // Continue without season ID - API calls will work without it
        }
      }
    } catch (error: any) {
      console.error('Error loading tournament:', error);
      setError(error.message || 'Failed to load tournament details');
    } finally {
      setLoading(false);
    }
  };

  // Load seasons for the tournament
  const loadSeasons = async () => {
    if (!id) return;

    try {
      setSeasonsLoading(true);
      const allSeasons = await getAllTournamentSeasons(id);
      // Check if tournament has DEFAULT season
      const defaultSeason = allSeasons.find((s: TournamentSeason) => s.seasonType === 'DEFAULT');
      setHasDefaultSeason(!!defaultSeason);
      // Filter to show only SEASON type seasons (not DEFAULT)
      const actualSeasons = allSeasons.filter((s: TournamentSeason) => s.seasonType === 'SEASON');
      setSeasons(actualSeasons);
    } catch (error: any) {
      console.error('Error loading seasons:', error);
      setSeasons([]);
      setHasDefaultSeason(false);
    } finally {
      setSeasonsLoading(false);
    }
  };

  // Load seasons when tournament is loaded and details tab is active
  useEffect(() => {
    if (activeTab === 'details' && tournament?._id) {
      loadSeasons();
    }
  }, [activeTab, tournament?._id]);

  // Handle add season button click
  const handleAddSeasonClick = () => {
    setEditingSeason(null);
    setIsEditingSeason(true);
    setSeasonFormData({
      name: '',
      startDate: '',
      endDate: '',
      location: tournament?.location || '',
      seasonYearRange: '',
      isFeaturedOnTv: false,
      showOnHomePage: true,
      tvSortOrder: '2',
      tvPreviewDuration: '',
      sortOrder: '2',
      tvAccessType: 'paid',
      recordingAccessType: 'free',
      highlightAccessType: 'free',
      matchPassEnabled: false,
      matchPassPrice: '',
      seasonPassEnabled: false,
      seasonPassPrice: '',
      tournamentPassEnabled: false,
    });
    setSeasonFormErrors({});
    setApiError(null);
    setSuccessMessage(null);
  };

  // Handle edit season button click
  const handleEditSeasonClick = (season: TournamentSeason) => {
    setEditingSeason(season);
    setIsEditingSeason(true);
    setSeasonFormData({
      name: season.seasonLabel || '',
      startDate: season.startDate ? season.startDate.split('T')[0] : '',
      endDate: season.endDate ? season.endDate.split('T')[0] : '',
      location: season.location || '',
      seasonYearRange: season.seasonYearRange || '',
      isFeaturedOnTv: season.isFeaturedOnTv ?? true,
      showOnHomePage: season.showOnHomePage ?? true,
      tvSortOrder: (season.tvSortOrder ?? 2).toString(),
      tvPreviewDuration: season.tvPreviewDuration?.toString() || '',
      sortOrder: (season.sortOrder ?? 2).toString(),
      tvAccessType: season.tvAccessType ?? 'paid',
      recordingAccessType: season.recordingAccessType ?? 'free',
      highlightAccessType: season.highlightAccessType ?? 'free',
      matchPassEnabled: season.tvPassConfig?.match.enabled ?? false,
      matchPassPrice: season.tvPassConfig?.match.pricing?.IN.amount?.toString() || '',
      seasonPassEnabled: season.tvPassConfig?.season.enabled ?? false,
      seasonPassPrice: season.tvPassConfig?.season.pricing?.IN.amount?.toString() || '',
      tournamentPassEnabled: season.tvPassConfig?.tournament.enabled ?? false,
    });
    setSeasonFormErrors({});
    setApiError(null);
    setSuccessMessage(null);
  };

  // Handle cancel season edit
  const handleCancelSeasonEdit = () => {
    setIsEditingSeason(false);
    setEditingSeason(null);
    setSeasonFormData({
      name: '',
      startDate: '',
      endDate: '',
      location: '',
      seasonYearRange: '',
      isFeaturedOnTv: false,
      showOnHomePage: true,
      tvSortOrder: '2',
      tvPreviewDuration: '',
      sortOrder: '2',
      tvAccessType: 'paid',
      recordingAccessType: 'free',
      highlightAccessType: 'free',
      matchPassEnabled: false,
      matchPassPrice: '',
      seasonPassEnabled: false,
      seasonPassPrice: '',
      tournamentPassEnabled: false,
    });
    setSeasonFormErrors({});
    setApiError(null);
  };

  // Check if season can be deleted (not ongoing or completed)
  const canDeleteSeason = (season: TournamentSeason): boolean => {
    const status = (season.status || '').toLowerCase();
    return status !== 'ongoing' && status !== 'completed';
  };

  // Handle delete season
  const handleDeleteSeason = async (season: TournamentSeason) => {
    // Double check if deletion is allowed
    if (!canDeleteSeason(season)) {
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${season.seasonLabel}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteTournamentSeason(season._id, getSport());
      setSuccessMessage('Season deleted successfully!');
      await loadSeasons();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      setApiError(error.message || 'Failed to delete season');
    }
  };

  const validateSeasonForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!seasonFormData.name || seasonFormData.name.trim().length === 0) {
      errors.name = 'Season name is required';
    }

    if (!seasonFormData.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (!seasonFormData.endDate) {
      errors.endDate = 'End date is required';
    }

    if (!seasonFormData.location || seasonFormData.location.trim().length === 0) {
      errors.location = 'Location is required';
    } else {
      if (!SEASON_LOCATION_PATTERN.test(seasonFormData.location.trim())) {
        errors.location = SEASON_LOCATION_ERROR_MESSAGE;
      }
    }

    if (!seasonFormData.seasonYearRange || seasonFormData.seasonYearRange.trim().length === 0) {
      errors.seasonYearRange = 'Season year range is required';
    } else {
      const yearRangePattern = /^(\d{4})(-\d{4})?$/;
      const trimmedRange = seasonFormData.seasonYearRange.trim();
      if (!yearRangePattern.test(trimmedRange)) {
        errors.seasonYearRange = 'Season year range must be in format "2025" or "2025-2026"';
      } else {
        if (trimmedRange.includes('-')) {
          const [startYear, endYear] = trimmedRange.split('-').map(Number);
          if (endYear < startYear) {
            errors.seasonYearRange = 'End year must be greater than or equal to start year';
          }
        }
      }
    }

    if (seasonFormData.startDate && seasonFormData.endDate) {
      const startDate = new Date(seasonFormData.startDate);
      const endDate = new Date(seasonFormData.endDate);
      if (endDate < startDate) {
        errors.endDate = 'End date must be after start date';
      }
    }

    setSeasonFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Calculate season year range from dates
  const calculateSeasonYearRange = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '';
    const startYear = new Date(startDate).getFullYear();
    const endYear = new Date(endDate).getFullYear();
    return `${startYear}-${endYear}`;
  };

  // Calculate next season number
  const calculateNextSeasonNumber = (existingSeasons: TournamentSeason[], hasDefault: boolean): number => {
    // If tournament has DEFAULT season and no SEASON type seasons exist yet, start from 2
    if (hasDefault && existingSeasons.length === 0) {
      return 2;
    }

    // If no seasons exist and no DEFAULT season, start from 1
    if (existingSeasons.length === 0) {
      return 1;
    }

    // Otherwise, find max seasonNumber from existing SEASON type seasons and add 1
    const maxSeasonNumber = Math.max(...existingSeasons.map(s => s.seasonNumber || 0));
    return maxSeasonNumber + 1;
  };

  // Toggle season expansion
  const toggleSeasonExpansion = (seasonId: string) => {
    setExpandedSeasons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(seasonId)) {
        newSet.delete(seasonId);
      } else {
        newSet.add(seasonId);
        // Set default tab to 'age-categories' if not set
        if (!seasonActiveTabs[seasonId]) {
          setSeasonActiveTabs(prevTabs => ({
            ...prevTabs,
            [seasonId]: 'age-categories'
          }));
        }
      }
      return newSet;
    });
  };

  // Set active tab for a season
  const setSeasonActiveTab = (seasonId: string, tab: TabType) => {
    // Prevent setting player-stats tab for badminton
    if (tab === 'player-stats' && isBadminton()) {
      return;
    }
    setSeasonActiveTabs(prev => ({
      ...prev,
      [seasonId]: tab
    }));
  };

  // Handle season form submission
  const handleSeasonFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateSeasonForm() || !tournament?._id) {
      return;
    }

    try {
      setSeasonFormLoading(true);
      setApiError(null);
      setSuccessMessage(null);

      const startDate = seasonFormData.startDate;
      const endDate = seasonFormData.endDate;
      const seasonYearRange = seasonFormData.seasonYearRange.trim() || calculateSeasonYearRange(startDate, endDate);
      const location = seasonFormData.location.trim();
      const seasonSettings = {
        isFeaturedOnTv: seasonFormData.isFeaturedOnTv,
        showOnHomePage: seasonFormData.showOnHomePage,
        tvSortOrder: Number(seasonFormData.tvSortOrder),
        tvPreviewDuration: seasonFormData.tvPreviewDuration === '' ? null : Number(seasonFormData.tvPreviewDuration),
        sortOrder: Number(seasonFormData.sortOrder),
        tvAccessType: seasonFormData.tvAccessType as 'free' | 'paid',
        recordingAccessType: seasonFormData.recordingAccessType as 'free' | 'paid',
        highlightAccessType: seasonFormData.highlightAccessType as 'free' | 'paid',
        tvPassConfig: buildTvPassConfig({
          matchEnabled: seasonFormData.tvAccessType === 'paid' && seasonFormData.matchPassEnabled,
          matchPrice: seasonFormData.matchPassPrice,
          seasonEnabled: seasonFormData.seasonPassEnabled,
          seasonPrice: seasonFormData.seasonPassPrice,
          tournamentEnabled: seasonFormData.tournamentPassEnabled,
        }),
      };

      if (editingSeason) {
        // Edit existing season - preserve seasonNumber and seasonType from existing season
        await editTournamentSeason(editingSeason._id, getSport(), {
          seasonType: editingSeason.seasonType || 'SEASON',
          seasonNumber: editingSeason.seasonNumber || 1,
          seasonLabel: seasonFormData.name.trim(),
          seasonYearRange: seasonYearRange,
          location: location,
          startDate: startDate,
          endDate: endDate,
          ...seasonSettings,
        });
        setSuccessMessage('Season updated successfully!');
      } else {
        // Add new season - calculate seasonNumber from existing seasons
        const nextSeasonNumber = calculateNextSeasonNumber(seasons, hasDefaultSeason);
        await addTournamentSeason(tournament._id, getSport(), {
          seasonType: 'SEASON',
          seasonNumber: nextSeasonNumber,
          seasonLabel: seasonFormData.name.trim(),
          seasonYearRange: seasonYearRange,
          location: location,
          startDate: startDate,
          endDate: endDate,
          ageCategories: [],
          milestonePayment: [],
          pricing: []
          ,
          ...seasonSettings
        });
        setSuccessMessage('Season added successfully!');

        // Navigate to seasons listing page after successful creation
        setTimeout(() => {
          navigate(`/manage-tournaments-fixtures/${tournament._id}/seasons`);
        }, 500);
        return; // Exit early to prevent further execution for add operation
      }

      // For edit operation, stay on current page and reload seasons
      setIsEditingSeason(false);
      setEditingSeason(null);
      await loadSeasons();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      setApiError(error.message || `Failed to ${editingSeason ? 'update' : 'add'} season`);
    } finally {
      setSeasonFormLoading(false);
    }
  };

  // Validation functions
  const validateField = (field: string, value: any, currentData?: TournamentPayload): string => {
    const data = currentData || inlineEditData;

    switch (field) {
      case 'name':
        if (!value || value.trim().length === 0) {
          return 'Tournament name is required';
        }
        if (value.trim().length < 3) {
          return 'Tournament name must be at least 3 characters long';
        }
        if (value.trim().length > 100) {
          return 'Tournament name must be less than 100 characters';
        }
        break;
      case 'shortName':
        if (!value || value.trim().length === 0) {
          return 'Short name is required';
        }
        if (value.trim().length < 2) {
          return 'Short name must be at least 2 characters long';
        }
        if (value.trim().length > 20) {
          return 'Short name must be less than 20 characters';
        }
        break;
      case 'sports':
        if (!value || value.trim().length === 0) {
          return 'Sports is required';
        }
        break;
      case 'status':
        if (!value || value.trim().length === 0) {
          return 'Status is required';
        }
        const validStatuses = ['upcoming', 'ongoing', 'completed', 'cancelled'];
        if (!validStatuses.includes(value)) {
          return 'Please select a valid status';
        }
        break;
      case 'startDate':
        if (!value) {
          return 'Start date is required';
        }
        // Only validate start date if it's actually being changed
        if (field === 'startDate') {
          const startDateObj = new Date(value);
          const todayObj = new Date();
          todayObj.setHours(0, 0, 0, 0);

          // Allow past dates for ongoing and completed tournaments
          const currentStatus = data.status || inlineEditData.status;
          if (startDateObj < todayObj && currentStatus === 'upcoming') {
            return 'Start date cannot be in the past for upcoming tournaments';
          }
        }
        break;
      case 'endDate':
        if (!value) {
          return 'End date is required';
        }
        // Only validate end date if it's actually being changed
        if (field === 'endDate') {
          if (data.startDate && value < data.startDate) {
            return 'End date must be after start date';
          }

          const endDateObj = new Date(value);
          const todayForEnd = new Date();
          todayForEnd.setHours(0, 0, 0, 0);

          // Allow past end dates for completed tournaments
          const currentStatus = data.status || inlineEditData.status;
          if (endDateObj < todayForEnd && currentStatus === 'upcoming') {
            return 'End date cannot be in the past for upcoming tournaments';
          }

          if (data.startDate) {
            const startDateForEnd = new Date(data.startDate);
            const daysDifference = (endDateObj.getTime() - startDateForEnd.getTime()) / (1000 * 3600 * 24);
            if (daysDifference > 365) {
              return 'Tournament duration cannot exceed 365 days';
            }
          }
        }
        break;
    }
    return '';
  };

  const validateForm = (data: TournamentPayload): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};

    // Exclude sports from validation since it's read-only
    const fieldsToValidate = ['name', 'shortName'];

    fieldsToValidate.forEach(field => {
      const error = validateField(field, data[field as keyof TournamentPayload], data);
      if (error) {
        errors[field] = error;
      }
    });

    return errors;
  };

  // Handle edit button click - Start inline editing
  const handleEditClick = () => {
    if (tournament) {
      setInlineEditData({
        _id: tournament._id,
        name: tournament.name,
        shortName: tournament.shortName,
        logo: tournament.logo || '',
        location: tournament.location,
        startDate: tournament.startDate,
        endDate: tournament.endDate,
        status: tournament.status,
        sports: tournament.sports,
        sortNo: 1
      });
      setIsEditing(true);
      // Clear any previous errors and messages
      setValidationErrors({});
      setApiError(null);
      setSuccessMessage(null);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setInlineEditData({
      name: '',
      shortName: '',
      logo: '',
      location: '',
      startDate: '',
      endDate: '',
      status: '',
      sports: '',
      sortNo: 1
    });
    // Clear validation errors and messages
    setValidationErrors({});
    setApiError(null);
    setSuccessMessage(null);
  };

  // Handle inline edit form submission
  const handleInlineEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineEditData._id) return;

    // Clear previous errors and messages
    setApiError(null);
    setSuccessMessage(null);

    // Validate form
    const errors = validateForm(inlineEditData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Clear validation errors if form is valid
    setValidationErrors({});

    try {
      setEditLoading(true);
      await editTournament(inlineEditData);
      setIsEditing(false);
      setSuccessMessage('Tournament updated successfully!');
      // Reload tournament details
      await loadTournamentDetails();
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error updating tournament:', err);

      // Handle different types of API errors
      let errorMessage = 'Failed to update tournament';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setApiError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  // Handle field change with validation
  const handleFieldChange = (field: string, value: any) => {
    const updatedData = { ...inlineEditData, [field]: value };
    setInlineEditData(updatedData);

    // Clear field-specific validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Clear API error when user makes changes
    if (apiError) {
      setApiError(null);
    }

    // Real-time validation for certain fields (exclude sports since it's read-only)
    // Only validate the specific field being changed, not all fields
    if (field === 'name' || field === 'shortName' || field === 'status' || field === 'startDate' || field === 'endDate') {
      const error = validateField(field, value, updatedData);
      if (error) {
        setValidationErrors(prev => ({ ...prev, [field]: error }));
      }
    }

    // If status is being changed, re-validate date fields since status affects date validation
    if (field === 'status') {
      // Re-validate start date if it exists
      if (updatedData.startDate) {
        const startDateError = validateField('startDate', updatedData.startDate, updatedData);
        if (startDateError) {
          setValidationErrors(prev => ({ ...prev, startDate: startDateError }));
        } else {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.startDate;
            return newErrors;
          });
        }
      }

      // Re-validate end date if it exists
      if (updatedData.endDate) {
        const endDateError = validateField('endDate', updatedData.endDate, updatedData);
        if (endDateError) {
          setValidationErrors(prev => ({ ...prev, endDate: endDateError }));
        } else {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.endDate;
            return newErrors;
          });
        }
      }
    }
  };

  // Handle edit form submission (modal)
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFormData._id) return;

    try {
      setEditLoading(true);
      await editTournament(editFormData);
      setShowEditModal(false);
      // Reload tournament details
      await loadTournamentDetails();
    } catch (err: any) {
      console.error('Error updating tournament:', err);
      alert('Failed to update tournament: ' + (err.message || 'Unknown error'));
    } finally {
      setEditLoading(false);
    }
  };

  // Check if tournament can be deleted (not ongoing or completed)
  const canDeleteTournament = (tournament: ManageTournament | null): boolean => {
    if (!tournament) return false;
    const status = (tournament.status || '').toLowerCase();
    return status !== 'ongoing' && status !== 'completed';
  };

  // Handle delete button click
  const handleDeleteClick = async () => {
    // Double check if deletion is allowed
    if (!canDeleteTournament(tournament) || !tournament?._id) {
      return;
    }

    if (window.confirm('Are you sure you want to delete this tournament? This action cannot be undone.')) {
      try {
        setIsDeletingTournament(true);
        setApiError(null);
        await deleteTournament(tournament._id);
        navigate('/manage-tournaments-fixtures');
      } catch (err: any) {
        console.error('Error deleting tournament:', err);
        setApiError(err.message || 'Failed to delete tournament. Please try again.');
      } finally {
        setIsDeletingTournament(false);
      }
    }
  };

  // Tab handler functions
  const handleAddAgeCategory = () => {
    console.log('Add age category');
  };

  const handleEditAgeCategory = () => {
    console.log('Edit age category');
  };

  const handleDeleteAgeCategory = () => {
    console.log('Delete age category');
  };

  // Refresh function for age categories
  const refreshAgeCategories = () => {
    console.log('Refreshing age categories...');
    setAgeCategoryRefresh(v => !v);
  };

  const handleAddTeam = () => {
    setTeamModalRefresh(v => !v);
  };

  const handleEditTeam = () => {
    setTeamModalRefresh(v => !v);
  };

  const handleDeleteTeam = () => {
    setTeamModalRefresh(v => !v);
  };

  const handleAddPlayer = async (payload: any) => {
    try {
      await addPlayer(payload, getSport());
      setPlayersRefresh(v => !v);
      setPlayerStatsRefresh(v => !v); // Also refresh player stats tab
    } catch (error) {
      console.error('Error adding player:', error);
    }
  };

  const handleEditPlayer = async (payload: any) => {
    try {
      await editPlayer(payload._id, payload);
      setPlayersRefresh(v => !v);
      setPlayerStatsRefresh(v => !v); // Also refresh player stats tab
    } catch (error) {
      console.error('Error editing player:', error);
    }
  };

  const handleDeletePlayer = async (playerId: string, sport: string) => {
    try {
      await deletePlayer(playerId, sport);
      setPlayersRefresh(v => !v);
      setPlayerStatsRefresh(v => !v); // Also refresh player stats tab
    } catch (error) {
      console.error('Error deleting player:', error);
    }
  };

  const handleAddFixture = () => {
    setFixturesRefresh(v => !v);
  };

  const handleEditFixture = () => {
    setFixturesRefresh(v => !v);
  };

  const handleDeleteFixture = () => {
    setFixturesRefresh(v => !v);
  };

  // Handle download Excel
  const handleDownloadExcel = async () => {
    // Open modal for filter selection
    setShowDownloadExcelModal(true);
    setExcelDownloadError(null);
    setExcelDownloadStartDate(null);
    setExcelDownloadEndDate(null);
    setExcelDownloadStatus('');

    // Fetch calendar events - always pass defaultSeasonId when available
    if (tournament?._id) {
      setLoadingCalendarEvents(true);
      try {
        const events = await getCalendarEvents(tournament._id, getSport(), defaultSeasonId || undefined);
        setCalendarEvents(events);
      } catch (err) {
        console.error('Error fetching calendar events:', err);
        setCalendarEvents([]);
      } finally {
        setLoadingCalendarEvents(false);
      }
    }
  };

  const handleExcelDownloadSubmit = async () => {
    if (!tournament?._id) {
      setExcelDownloadError('Tournament ID not found');
      return;
    }

    // Validation - tournamentSeason is now required
    if (!defaultSeasonId) {
      setExcelDownloadError('Tournament season ID not found. Please wait for tournament details to load.');
      return;
    }

    // Validation
    if (!excelDownloadStartDate) {
      setExcelDownloadError('Please select a start date');
      return;
    }

    if (!excelDownloadStatus) {
      setExcelDownloadError('Please select a status');
      return;
    }

    // If only start date is selected, use it as both start and end date
    const startDate = excelDownloadStartDate;
    const endDate = excelDownloadEndDate || excelDownloadStartDate;

    // Format dates as YYYY-MM-DD
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setIsDownloadingExcel(true);
    setExcelDownloadError(null);

    try {
      // Get the S3 URL from the API - tournamentSeason is now required
      const excelUrl = await downloadFixturesExcelWithFilters(tournament._id, {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        status: excelDownloadStatus as 'pending' | 'completed',
        sports: getSport(),
        tournamentSeason: defaultSeasonId, // Always pass tournamentSeason ID
      });

      if (!excelUrl || typeof excelUrl !== 'string') {
        throw new Error('Invalid Excel URL received from server');
      }

      // Generate filename with tournament name and date range
      const tournamentName = tournament?.name || 'tournament';
      const sanitizedTournamentName = tournamentName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const dateRange = excelDownloadEndDate
        ? `${formatDate(startDate)}-to-${formatDate(endDate)}`
        : formatDate(startDate);
      const filename = `fixtures-${sanitizedTournamentName}-${dateRange}.xlsx`;

      // Download the file from S3 URL
      // Try to fetch and download as blob first (allows custom filename)
      try {
        const fileResponse = await fetch(excelUrl, {
          method: 'GET',
          mode: 'cors',
        });

        if (!fileResponse.ok) {
          throw new Error(`Failed to download file: ${fileResponse.status} ${fileResponse.statusText}`);
        }

        // Get the file as a blob
        const blob = await fileResponse.blob();

        if (!blob || blob.size === 0) {
          throw new Error('Downloaded file is empty');
        }

        // Create a blob URL and trigger download
        const blobUrl = URL.createObjectURL(blob);

        // Create and configure download link
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = filename;
        downloadLink.style.position = 'fixed';
        downloadLink.style.top = '-1000px';
        downloadLink.style.left = '-1000px';

        // Append to body
        document.body.appendChild(downloadLink);

        // Trigger download immediately
        downloadLink.click();

        // Clean up after a delay
        setTimeout(() => {
          if (document.body.contains(downloadLink)) {
            document.body.removeChild(downloadLink);
          }
          URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (fetchError: any) {
        // If fetch fails (e.g., CORS issue), fall back to direct download
        console.warn('Fetch failed, using direct download:', fetchError);
        const directLink = document.createElement('a');
        directLink.href = excelUrl;
        directLink.download = filename;
        directLink.target = '_blank';
        directLink.style.position = 'fixed';
        directLink.style.top = '-1000px';
        directLink.style.left = '-1000px';
        document.body.appendChild(directLink);
        directLink.click();
        setTimeout(() => {
          if (document.body.contains(directLink)) {
            document.body.removeChild(directLink);
          }
        }, 100);
      }

      // Close modal and reset form
      setShowDownloadExcelModal(false);
      setExcelDownloadStartDate(null);
      setExcelDownloadEndDate(null);
      setExcelDownloadStatus('');
    } catch (err: any) {
      console.error('Error downloading Excel:', err);
      setExcelDownloadError('Failed to download Excel file: ' + (err.message || 'Unknown error'));
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const handleCloseDownloadExcelModal = () => {
    setShowDownloadExcelModal(false);
    setExcelDownloadStartDate(null);
    setExcelDownloadEndDate(null);
    setExcelDownloadStatus('');
    setExcelDownloadError(null);
  };


  const getTabTitle = () => {
    switch (activeTab) {
      case 'details':
        return 'Tournament Details';
      case 'age-categories':
        return 'Age Categories';
      case 'teams':
        return 'Teams';
      case 'players':
        return 'Players';
      case 'player-stats':
        return 'Player Stats';
      case 'match-management':
        return 'Match Management';
      case 'standings':
        return 'Standings';
      default:
        return 'Tournament Details';
    }
  };

  // Access denied for non-authorized users
  if (!canAccessFixtures) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <h4>Access Denied</h4>
          <p>Only super-admin and operations-admin users can access this module.</p>
        </div>
      </Container>
    );
  }

  const breadcrumbItems = [
    { label: 'Manage Tournaments', onClick: () => navigate('/manage-tournaments-fixtures') },
    { label: tournament?.name || 'Tournament Details', active: true }
  ];

  if (loading) {
    return (
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <ShimmerLoader type="table" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <div className="text-center py-5">
          <h4>Error Loading Tournament</h4>
          <p>{error}</p>
          <Button variant="primary" onClick={loadTournamentDetails}>
            Try Again
          </Button>
        </div>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <div className="text-center py-5">
          <h4>Tournament Not Found</h4>
          <p>The requested tournament could not be found.</p>
          <Button variant="primary" onClick={() => navigate('/manage-tournaments-fixtures')}>
            Back to Tournaments
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        {/* Tournament Header - Reusable Component */}
        <div style={{ padding: '0 15px' }}>
          <TournamentHeader
            tournament={tournament}
            onGoBack={() => navigate('/manage-tournaments-fixtures')}
          />
        </div>

        {/* Tabs Navigation - Clean Design */}
        <div style={{ padding: '0 15px' }}>
          <Nav variant="tabs" className="tournament-tabs mb-4" style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
            <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'details'}
                onClick={() => setActiveTab('details')}
              >
                <i className="ri-information-line me-2"></i>
                Details
              </Nav.Link>
            </Nav.Item>
            <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'age-categories'} onClick={() => setActiveTab('age-categories')}><i className="ri-group-line me-2" />Age Category</Nav.Link></Nav.Item>
            <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'teams'}
                onClick={() => setActiveTab('teams')}
              >
                <i className="ri-team-line me-2"></i>
                Teams
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'players'}
                onClick={() => setActiveTab('players')}
              >
                <i className="ri-user-line me-2"></i>
                Players
              </Nav.Link>
            </Nav.Item>
            {supports('standings') && <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'standings'} onClick={() => setActiveTab('standings')}><i className="ri-bar-chart-line me-2" />Standings</Nav.Link></Nav.Item>}
            {supports('fixtures') && <Nav.Item><Nav.Link className="tournament-tab" active={activeTab === 'match-management'} onClick={() => setActiveTab('match-management')}><i className="ri-calendar-event-line me-2" />Matches</Nav.Link></Nav.Item>}
            {!isBadminton() && supports('playerStats') && (
              <Nav.Item>
                <Nav.Link
                  className="tournament-tab"
                  active={activeTab === 'player-stats'}
                  onClick={() => setActiveTab('player-stats')}
                >
                  <i className="ri-line-chart-line me-2"></i>
                  Player stats
                </Nav.Link>
              </Nav.Item>
            )}
            {supports('content') && <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'videos'}
                onClick={() => setActiveTab('videos')}
              >
                <i className="ri-video-line me-2"></i>
                Videos
              </Nav.Link>
            </Nav.Item>}
            <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'settings'}
                onClick={() => setActiveTab('settings')}
              >
                <i className="ri-settings-3-line me-2"></i>
                Settings
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'voting'}
                onClick={() => setActiveTab('voting')}
              >
                <i className="ri-thumb-up-line me-2"></i>
                Voting
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className="tournament-tab"
                active={activeTab === 'predictions'}
                onClick={() => setActiveTab('predictions')}
              >
                <i className="ri-question-answer-line me-2"></i>
                Predictions
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {/* Tab Content */}
        <div style={{ padding: '0 15px' }}>
          {activeTab === 'details' ? (
            <div>
              {/* Add New Season Banner - Show for DEFAULT season tournaments when no SEASON seasons exist */}
              {hasDefaultSeason && seasons.length === 0 && !isEditingSeason && (
                <div className="mb-4" style={{ backgroundColor: '#E6F1FE', borderRadius: '8px', padding: '20px' }}>
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <img
                          src={PlusIcon}
                          alt="Plus"
                          style={{ width: '24px', height: '24px', flexShrink: 0, marginTop: '2px' }}
                        />
                        <div>
                          <div
                            style={{
                              fontFamily: 'Font/Family/Special Body',
                              fontWeight: 600,
                              fontSize: '16px',
                              lineHeight: '100%',
                              letterSpacing: '0%',
                              color: '#212529',
                              marginBottom: '4px'
                            }}
                          >
                            Add New Season
                          </div>
                          <div
                            style={{
                              fontFamily: 'Font/Family/Special Body',
                              fontWeight: 500,
                              fontSize: '14px',
                              lineHeight: '100%',
                              letterSpacing: '0%',
                              color: '#6c757d',
                              marginTop: '4px'
                            }}
                          >
                            Create a new season to manage teams, players, and matches separately.
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="text-end">
                      <Button
                        variant="primary"
                        onClick={handleAddSeasonClick}
                        style={{
                          borderRadius: '6px',
                          padding: '10px 20px',
                          fontWeight: '500'
                        }}
                      >
                        <i className="ri-add-line me-2"></i>
                        Add New Season
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Inline Season Form - Show when editing/adding season for DEFAULT tournaments */}
              {hasDefaultSeason && isEditingSeason && (
                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0 card-title">{editingSeason ? 'Edit Season' : 'Add New Season'}</h4>
                  </Card.Header>
                  <Card.Body>
                    <Form onSubmit={handleSeasonFormSubmit}>
                      <Row>
                        <Col md={12}>
                          <div className="mb-4">
                            <label className="form-label" style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              fontWeight: '500',
                              marginBottom: '8px'
                            }}>
                              Season Name <span className="text-danger">*</span>
                            </label>
                            <Form.Control
                              type="text"
                              value={seasonFormData.name}
                              onChange={(e) => {
                                setSeasonFormData({ ...seasonFormData, name: e.target.value });
                                if (seasonFormErrors.name) {
                                  setSeasonFormErrors({ ...seasonFormErrors, name: '' });
                                }
                              }}
                              isInvalid={!!seasonFormErrors.name}
                              placeholder="Enter season name (e.g., season-1)"
                              style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529',
                                borderColor: seasonFormErrors.name ? '#dc3545' : undefined
                              }}
                            />
                            {seasonFormErrors.name && (
                              <Form.Control.Feedback type="invalid">
                                {seasonFormErrors.name}
                              </Form.Control.Feedback>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-4">
                            <label className="form-label" style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              fontWeight: '500',
                              marginBottom: '8px'
                            }}>
                              Start Date <span className="text-danger">*</span>
                            </label>
                            <Form.Control
                              type="date"
                              value={seasonFormData.startDate}
                              onChange={(e) => {
                                setSeasonFormData({ ...seasonFormData, startDate: e.target.value });
                                if (seasonFormErrors.startDate) {
                                  setSeasonFormErrors({ ...seasonFormErrors, startDate: '' });
                                }
                                if (seasonFormData.endDate) {
                                  const endDateError = new Date(seasonFormData.endDate) < new Date(e.target.value)
                                    ? 'End date must be after start date'
                                    : '';
                                  if (endDateError) {
                                    setSeasonFormErrors({ ...seasonFormErrors, endDate: endDateError });
                                  } else if (seasonFormErrors.endDate) {
                                    const newErrors = { ...seasonFormErrors };
                                    delete newErrors.endDate;
                                    setSeasonFormErrors(newErrors);
                                  }
                                }
                              }}
                              isInvalid={!!seasonFormErrors.startDate}
                              style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529',
                                borderColor: seasonFormErrors.startDate ? '#dc3545' : undefined
                              }}
                            />
                            {seasonFormErrors.startDate && (
                              <Form.Control.Feedback type="invalid">
                                {seasonFormErrors.startDate}
                              </Form.Control.Feedback>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-4">
                            <label className="form-label" style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              fontWeight: '500',
                              marginBottom: '8px'
                            }}>
                              End Date <span className="text-danger">*</span>
                            </label>
                            <Form.Control
                              type="date"
                              value={seasonFormData.endDate}
                              min={seasonFormData.startDate || undefined}
                              onChange={(e) => {
                                setSeasonFormData({ ...seasonFormData, endDate: e.target.value });
                                if (seasonFormErrors.endDate) {
                                  setSeasonFormErrors({ ...seasonFormErrors, endDate: '' });
                                }
                                if (seasonFormData.startDate && new Date(e.target.value) < new Date(seasonFormData.startDate)) {
                                  setSeasonFormErrors({ ...seasonFormErrors, endDate: 'End date must be after start date' });
                                } else if (seasonFormErrors.endDate) {
                                  const newErrors = { ...seasonFormErrors };
                                  delete newErrors.endDate;
                                  setSeasonFormErrors(newErrors);
                                }
                              }}
                              isInvalid={!!seasonFormErrors.endDate}
                              style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529',
                                borderColor: seasonFormErrors.endDate ? '#dc3545' : undefined
                              }}
                            />
                            {seasonFormErrors.endDate && (
                              <Form.Control.Feedback type="invalid">
                                {seasonFormErrors.endDate}
                              </Form.Control.Feedback>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-4">
                            <label className="form-label" style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              fontWeight: '500',
                              marginBottom: '8px'
                            }}>
                              Location <span className="text-danger">*</span>
                            </label>
                            <Form.Control
                              type="text"
                              value={seasonFormData.location}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (SEASON_LOCATION_INPUT_PATTERN.test(value)) {
                                  setSeasonFormData({ ...seasonFormData, location: value });
                                  if (seasonFormErrors.location) {
                                    setSeasonFormErrors({ ...seasonFormErrors, location: '' });
                                  }
                                }
                              }}
                              isInvalid={!!seasonFormErrors.location}
                              placeholder="Enter location"
                              style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529',
                                borderColor: seasonFormErrors.location ? '#dc3545' : undefined
                              }}
                            />
                            {seasonFormErrors.location && (
                              <Form.Control.Feedback type="invalid">
                                {seasonFormErrors.location}
                              </Form.Control.Feedback>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-4">
                            <label className="form-label" style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              fontWeight: '500',
                              marginBottom: '8px'
                            }}>
                              Season Year Range <span className="text-danger">*</span>
                            </label>
                            <Form.Control
                              type="text"
                              value={seasonFormData.seasonYearRange}
                              onChange={(e) => {
                                const value = e.target.value;
                                const yearRangePattern = /^[\d\-]*$/;
                                if (yearRangePattern.test(value) || value === '') {
                                  setSeasonFormData({ ...seasonFormData, seasonYearRange: value });
                                  if (seasonFormErrors.seasonYearRange) {
                                    setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: '' });
                                  }
                                }
                              }}
                              onBlur={() => {
                                // Validate on blur
                                const trimmedRange = seasonFormData.seasonYearRange.trim();
                                if (trimmedRange) {
                                  const yearRangePattern = /^(\d{4})(-\d{4})?$/;
                                  if (!yearRangePattern.test(trimmedRange)) {
                                    setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: 'Season year range must be in format "2025" or "2025-2026"' });
                                  } else if (trimmedRange.includes('-')) {
                                    const [startYear, endYear] = trimmedRange.split('-').map(Number);
                                    if (endYear < startYear) {
                                      setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: 'End year must be greater than or equal to start year' });
                                    }
                                  }
                                }
                              }}
                              isInvalid={!!seasonFormErrors.seasonYearRange}
                              placeholder="e.g., 2025 or 2025-2026"
                              style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529',
                                borderColor: seasonFormErrors.seasonYearRange ? '#dc3545' : undefined
                              }}
                            />
                            {seasonFormErrors.seasonYearRange && (
                              <Form.Control.Feedback type="invalid">
                                {seasonFormErrors.seasonYearRange}
                              </Form.Control.Feedback>
                            )}
                            <small className="text-muted">
                              Format: YYYY (e.g., 2025) or YYYY-YYYY (e.g., 2025-2026)
                            </small>
                          </div>
                        </Col>
                      </Row>
                      <Row className="g-3 mb-4">
                        <Col md={12}><h6 className="mb-3">TV & Display Settings</h6></Col>
                        <Col md={12}><Row><Col md={4}><Form.Check type="switch" label="Featured on TV" checked={seasonFormData.isFeaturedOnTv} onChange={(e) => setSeasonFormData({ ...seasonFormData, isFeaturedOnTv: e.target.checked })} /></Col><Col md={4}><Form.Check type="switch" label="Show on Home Page" checked={seasonFormData.showOnHomePage} onChange={(e) => setSeasonFormData({ ...seasonFormData, showOnHomePage: e.target.checked })} /></Col></Row></Col>
                        <Col md={12}><Row>{[['tvAccessType', 'TV Access Type'], ['recordingAccessType', 'Recording Access Type'], ['highlightAccessType', 'Highlight Access Type']].map(([field, label]) => <Col md={4} key={field}><Form.Label>{label}</Form.Label><Form.Select value={(seasonFormData as any)[field]} onChange={(e) => setSeasonFormData({ ...seasonFormData, [field]: e.target.value })}><option value="free">Free</option><option value="paid">Paid</option></Form.Select></Col>)}</Row></Col>
                        {seasonFormData.tvAccessType === 'paid' && (
                        <Col md={12}><Row>{seasonFormData.tvAccessType === 'paid' && <Col md={4}><Form.Check type="switch" label="Enable Match TV Pass" checked={seasonFormData.matchPassEnabled} onChange={(e) => setSeasonFormData({ ...seasonFormData, matchPassEnabled: e.target.checked })} />{seasonFormData.matchPassEnabled && <><Form.Label className="mt-2">Match override price (INR)</Form.Label><Form.Control type="number" min="0" step="0.01" value={seasonFormData.matchPassPrice} onChange={(e) => setSeasonFormData({ ...seasonFormData, matchPassPrice: e.target.value })} placeholder="Optional" /></>}</Col>}<Col md={4}><Form.Check type="switch" label="Enable Season TV Pass" checked={seasonFormData.seasonPassEnabled} onChange={(e) => setSeasonFormData({ ...seasonFormData, seasonPassEnabled: e.target.checked })} />{seasonFormData.seasonPassEnabled && <><Form.Label className="mt-2">Season override price (INR)</Form.Label><Form.Control type="number" min="0" step="0.01" value={seasonFormData.seasonPassPrice} onChange={(e) => setSeasonFormData({ ...seasonFormData, seasonPassPrice: e.target.value })} placeholder="Optional" /></>}</Col><Col md={4}><Form.Check type="switch" label="Enable Tournament TV Pass" checked={seasonFormData.tournamentPassEnabled} onChange={(e) => setSeasonFormData({ ...seasonFormData, tournamentPassEnabled: e.target.checked })} /></Col></Row></Col>
                        )}
                        <Col md={12}><Row><Col md={4}><Form.Label>Sort Order</Form.Label><Form.Control type="number" min="1" value={seasonFormData.sortOrder} onChange={(e) => setSeasonFormData({ ...seasonFormData, sortOrder: e.target.value })} /></Col><Col md={4}><Form.Label>TV Preview Duration (secs)</Form.Label><Form.Control type="number" min="0" value={seasonFormData.tvPreviewDuration} onChange={(e) => setSeasonFormData({ ...seasonFormData, tvPreviewDuration: e.target.value })} /></Col></Row></Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleCancelSeasonEdit}
                          disabled={seasonFormLoading}
                          style={{ borderRadius: '6px' }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          type="submit"
                          disabled={seasonFormLoading}
                          style={{ borderRadius: '6px' }}
                        >
                          {seasonFormLoading ? 'Saving...' : (editingSeason ? 'Update Season' : 'Add Season')}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              )}

              {/* Success/Error Messages for DEFAULT season tournaments */}
              {hasDefaultSeason && (successMessage || apiError) && (
                <div className="mb-4">
                  {successMessage && (
                    <div className="alert alert-success alert-dismissible fade show" role="alert">
                      <i className="ri-check-line me-2"></i>
                      {successMessage}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setSuccessMessage(null)}
                      ></button>
                    </div>
                  )}
                  {apiError && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                      <i className="ri-error-warning-line me-2"></i>
                      {apiError}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setApiError(null)}
                      ></button>
                    </div>
                  )}
                </div>
              )}

              {/* Tournament Details Tab - Match Screenshot Design with Inline Editing */}
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 card-title">Details</h4>
                  <div>
                    {!isEditing ? (
                      <>
                        {/* Only show Add New Season button in header if NOT a DEFAULT season tournament */}
                        {!hasDefaultSeason && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddSeasonClick}
                            className="me-2"
                            style={{ borderRadius: '6px' }}
                          >
                            <i className="ri-add-line me-1"></i>
                            Add New Season
                          </Button>
                        )}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleEditClick}
                          className="me-2"
                          style={{ borderRadius: '6px' }}
                        >
                          <i className="ri-pencil-line me-1"></i>
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleDeleteClick}
                          disabled={!canDeleteTournament(tournament) || isDeletingTournament}
                          title={canDeleteTournament(tournament) ? "Delete Tournament" : "Cannot delete ongoing or completed tournaments"}
                          style={{
                            borderRadius: '6px',
                            opacity: canDeleteTournament(tournament) && !isDeletingTournament ? 1 : 0.5,
                            cursor: canDeleteTournament(tournament) && !isDeletingTournament ? 'pointer' : 'not-allowed'
                          }}
                        >
                          <i className="ri-delete-bin-line me-1"></i>
                          {isDeletingTournament ? 'Deleting...' : 'Delete'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleCancelEdit}
                          className="me-2"
                          style={{ borderRadius: '6px' }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleInlineEditSubmit}
                          disabled={editLoading}
                          style={{ borderRadius: '6px' }}
                        >
                          {editLoading ? 'Saving...' : 'Save'}
                        </Button>
                      </>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  {/* Success Message */}
                  {successMessage && (
                    <div className="alert alert-success alert-dismissible fade show" role="alert">
                      <i className="ri-check-line me-2"></i>
                      {successMessage}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setSuccessMessage(null)}
                      ></button>
                    </div>
                  )}

                  {/* API Error Message */}
                  {apiError && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                      <i className="ri-error-warning-line me-2"></i>
                      {apiError}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setApiError(null)}
                      ></button>
                    </div>
                  )}

                  {/* Validation Errors Summary */}
                  {isEditing && Object.keys(validationErrors).length > 0 && (
                    <div className="alert alert-warning alert-dismissible fade show" role="alert">
                      <i className="ri-alert-line me-2"></i>
                      <strong>Please fix the following errors:</strong>
                      <ul className="mb-0 mt-2">
                        {Object.entries(validationErrors).map(([field, error]) => (
                          <li key={field}>
                            <strong>{field.charAt(0).toUpperCase() + field.slice(1)}:</strong> {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {isEditing ? (
                    /* Inline Edit Form */
                    <Form onSubmit={handleInlineEditSubmit}>
                      <Row>
                        <Col md={6}>
                          <LogoUpload
                            currentLogo={inlineEditData.logo}
                            onLogoChange={(logoUrl) => handleFieldChange('logo', logoUrl)}
                            onLogoRemove={() => handleFieldChange('logo', '')}
                            isEditMode={true}
                            context="tournaments/logo"
                            size={60}
                            title="Tournament Logo"
                            label="Tournament Logo"
                          />
                        </Col>
                        <Col md={6}>
                          <div className="mb-4">
                            <label className="form-label" style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              fontWeight: '500',
                              marginBottom: '8px'
                            }}>
                              Tournament Name <span className="text-danger">*</span>
                            </label>
                            <Form.Control
                              type="text"
                              value={inlineEditData.name}
                              onChange={(e) => handleFieldChange('name', e.target.value)}
                              isInvalid={!!validationErrors.name}
                              style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529',
                                borderColor: validationErrors.name ? '#dc3545' : undefined
                              }}
                              placeholder="Enter tournament name"
                            />
                            {validationErrors.name && (
                              <Form.Control.Feedback type="invalid">
                                {validationErrors.name}
                              </Form.Control.Feedback>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-4">
                            <label className="form-label" style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              fontWeight: '500',
                              marginBottom: '8px'
                            }}>
                              Tournament Short Name <span className="text-danger">*</span>
                            </label>
                            <Form.Control
                              type="text"
                              value={inlineEditData.shortName}
                              onChange={(e) => handleFieldChange('shortName', e.target.value)}
                              isInvalid={!!validationErrors.shortName}
                              style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529',
                                borderColor: validationErrors.shortName ? '#dc3545' : undefined
                              }}
                              placeholder="Enter short name"
                            />
                            {validationErrors.shortName && (
                              <Form.Control.Feedback type="invalid">
                                {validationErrors.shortName}
                              </Form.Control.Feedback>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-4">
                            <label className="form-label" style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              fontWeight: '500',
                              marginBottom: '8px'
                            }}>
                              Sports
                            </label>
                            <Form.Control
                              type="text"
                              value={inlineEditData.sports}
                              readOnly
                              style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#6c757d',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                cursor: 'not-allowed'
                              }}
                              placeholder="Sports type (read-only)"
                            />
                            <small className="text-muted">
                              <i className="ri-lock-line me-1"></i>
                              Sports field cannot be edited
                            </small>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  ) : (
                    /* Display Mode */
                    <Row>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Tournament Logo
                          </label>
                          <div>
                            {tournament.logo ? (
                              <img
                                src={getTournamentLogoUrl(tournament) || ''}
                                alt="Tournament Logo"
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '2px solid #e9ecef'
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '50%',
                                  backgroundColor: '#f8f9fa',
                                  border: '2px solid #dee2e6',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <i className="ri-image-line" style={{ fontSize: '1.5rem', color: '#6c757d' }}></i>
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Tournament Name
                          </label>
                          <p style={{
                            fontSize: '16px',
                            fontWeight: '500',
                            color: '#212529',
                            margin: '0'
                          }}>
                            {tournament.name}
                          </p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Tournament Short Name
                          </label>
                          <p style={{
                            fontSize: '16px',
                            fontWeight: '500',
                            color: '#212529',
                            margin: '0'
                          }}>
                            {tournament.shortName}
                          </p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{
                            fontSize: '14px',
                            color: '#6c757d',
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Sports
                          </label>
                          <p style={{
                            fontSize: '16px',
                            fontWeight: '500',
                            color: '#212529',
                            margin: '0'
                          }}>
                            {tournament.sports}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>

              {/* Seasons Listing Section - Only show if no DEFAULT season or if there are SEASON type seasons */}
              {(!hasDefaultSeason || seasons.length > 0) && (
                <Card className="mt-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0 card-title">Seasons</h4>
                  </Card.Header>
                  <Card.Body>
                    {/* Success Message */}
                    {successMessage && (
                      <div className="alert alert-success alert-dismissible fade show" role="alert">
                        <i className="ri-check-line me-2"></i>
                        {successMessage}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setSuccessMessage(null)}
                        ></button>
                      </div>
                    )}

                    {/* API Error Message */}
                    {apiError && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <i className="ri-error-warning-line me-2"></i>
                        {apiError}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setApiError(null)}
                        ></button>
                      </div>
                    )}

                    {isEditingSeason ? (
                      /* Inline Season Form */
                      <Form onSubmit={handleSeasonFormSubmit}>
                        <Row className="g-3 mb-4">
                      <Col md={12}><h6 className="mb-3">TV & Display Settings</h6></Col>
                      <Col md={12}><Row><Col md={4}><Form.Check type="switch" label="Featured on TV" checked={seasonFormData.isFeaturedOnTv} onChange={(e) => setSeasonFormData({ ...seasonFormData, isFeaturedOnTv: e.target.checked })} /></Col><Col md={4}><Form.Check type="switch" label="Show on Home Page" checked={seasonFormData.showOnHomePage} onChange={(e) => setSeasonFormData({ ...seasonFormData, showOnHomePage: e.target.checked })} /></Col></Row></Col>
                      <Col md={12}><Row>{[['tvAccessType', 'TV Access Type'], ['recordingAccessType', 'Recording Access Type'], ['highlightAccessType', 'Highlight Access Type']].map(([field, label]) => <Col md={4} key={field}><Form.Label>{label}</Form.Label><Form.Select value={(seasonFormData as any)[field]} onChange={(e) => setSeasonFormData({ ...seasonFormData, [field]: e.target.value })}><option value="free">Free</option><option value="paid">Paid</option></Form.Select></Col>)}</Row></Col>
                      {seasonFormData.tvAccessType === 'paid' && (
                      <Col md={12}><Row>{seasonFormData.tvAccessType === 'paid' && <Col md={4}><Form.Check type="switch" label="Enable Match TV Pass" checked={seasonFormData.matchPassEnabled} onChange={(e) => setSeasonFormData({ ...seasonFormData, matchPassEnabled: e.target.checked })} />{seasonFormData.matchPassEnabled && <><Form.Label className="mt-2">Match override price (INR)</Form.Label><Form.Control type="number" min="0" step="0.01" value={seasonFormData.matchPassPrice} onChange={(e) => setSeasonFormData({ ...seasonFormData, matchPassPrice: e.target.value })} placeholder="Optional" /></>}</Col>}<Col md={4}><Form.Check type="switch" label="Enable Season TV Pass" checked={seasonFormData.seasonPassEnabled} onChange={(e) => setSeasonFormData({ ...seasonFormData, seasonPassEnabled: e.target.checked })} />{seasonFormData.seasonPassEnabled && <><Form.Label className="mt-2">Season override price (INR)</Form.Label><Form.Control type="number" min="0" step="0.01" value={seasonFormData.seasonPassPrice} onChange={(e) => setSeasonFormData({ ...seasonFormData, seasonPassPrice: e.target.value })} placeholder="Optional" /></>}</Col><Col md={4}><Form.Check type="switch" label="Enable Tournament TV Pass" checked={seasonFormData.tournamentPassEnabled} onChange={(e) => setSeasonFormData({ ...seasonFormData, tournamentPassEnabled: e.target.checked })} /></Col></Row></Col>
                      )}
                      <Col md={12}><Row><Col md={4}><Form.Label>Sort Order</Form.Label><Form.Control type="number" min="1" value={seasonFormData.sortOrder} onChange={(e) => setSeasonFormData({ ...seasonFormData, sortOrder: e.target.value })} /></Col><Col md={4}><Form.Label>TV Preview Duration (secs)</Form.Label><Form.Control type="number" min="0" value={seasonFormData.tvPreviewDuration} onChange={(e) => setSeasonFormData({ ...seasonFormData, tvPreviewDuration: e.target.value })} /></Col></Row></Col>
                    </Row>
                    <Row>
                          <Col md={12}>
                            <div className="mb-4">
                              <label className="form-label" style={{
                                fontSize: '14px',
                                color: '#6c757d',
                                fontWeight: '500',
                                marginBottom: '8px'
                              }}>
                                Season Name <span className="text-danger">*</span>
                              </label>
                              <Form.Control
                                type="text"
                                value={seasonFormData.name}
                                onChange={(e) => {
                                  setSeasonFormData({ ...seasonFormData, name: e.target.value });
                                  if (seasonFormErrors.name) {
                                    setSeasonFormErrors({ ...seasonFormErrors, name: '' });
                                  }
                                }}
                                isInvalid={!!seasonFormErrors.name}
                                placeholder="Enter season name (e.g., season-1)"
                                style={{
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  color: '#212529',
                                  borderColor: seasonFormErrors.name ? '#dc3545' : undefined
                                }}
                              />
                              {seasonFormErrors.name && (
                                <Form.Control.Feedback type="invalid">
                                  {seasonFormErrors.name}
                                </Form.Control.Feedback>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-4">
                              <label className="form-label" style={{
                                fontSize: '14px',
                                color: '#6c757d',
                                fontWeight: '500',
                                marginBottom: '8px'
                              }}>
                                Start Date <span className="text-danger">*</span>
                              </label>
                              <Form.Control
                                type="date"
                                value={seasonFormData.startDate}
                                onChange={(e) => {
                                  setSeasonFormData({ ...seasonFormData, startDate: e.target.value });
                                  if (seasonFormErrors.startDate) {
                                    setSeasonFormErrors({ ...seasonFormErrors, startDate: '' });
                                  }
                                  if (seasonFormData.endDate) {
                                    const endDateError = new Date(seasonFormData.endDate) < new Date(e.target.value)
                                      ? 'End date must be after start date'
                                      : '';
                                    if (endDateError) {
                                      setSeasonFormErrors({ ...seasonFormErrors, endDate: endDateError });
                                    } else if (seasonFormErrors.endDate) {
                                      const newErrors = { ...seasonFormErrors };
                                      delete newErrors.endDate;
                                      setSeasonFormErrors(newErrors);
                                    }
                                  }
                                }}
                                isInvalid={!!seasonFormErrors.startDate}
                                style={{
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  color: '#212529',
                                  borderColor: seasonFormErrors.startDate ? '#dc3545' : undefined
                                }}
                              />
                              {seasonFormErrors.startDate && (
                                <Form.Control.Feedback type="invalid">
                                  {seasonFormErrors.startDate}
                                </Form.Control.Feedback>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-4">
                              <label className="form-label" style={{
                                fontSize: '14px',
                                color: '#6c757d',
                                fontWeight: '500',
                                marginBottom: '8px'
                              }}>
                                End Date <span className="text-danger">*</span>
                              </label>
                              <Form.Control
                                type="date"
                                value={seasonFormData.endDate}
                                min={seasonFormData.startDate || undefined}
                                onChange={(e) => {
                                  setSeasonFormData({ ...seasonFormData, endDate: e.target.value });
                                  if (seasonFormErrors.endDate) {
                                    setSeasonFormErrors({ ...seasonFormErrors, endDate: '' });
                                  }
                                  if (seasonFormData.startDate && new Date(e.target.value) < new Date(seasonFormData.startDate)) {
                                    setSeasonFormErrors({ ...seasonFormErrors, endDate: 'End date must be after start date' });
                                  } else if (seasonFormErrors.endDate) {
                                    const newErrors = { ...seasonFormErrors };
                                    delete newErrors.endDate;
                                    setSeasonFormErrors(newErrors);
                                  }
                                  // Auto-calculate season year range if not manually set
                                  if (seasonFormData.startDate && !seasonFormData.seasonYearRange) {
                                    const calculatedRange = calculateSeasonYearRange(seasonFormData.startDate, e.target.value);
                                    setSeasonFormData(prev => ({ ...prev, seasonYearRange: calculatedRange }));
                                  }
                                }}
                                isInvalid={!!seasonFormErrors.endDate}
                                style={{
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  color: '#212529',
                                  borderColor: seasonFormErrors.endDate ? '#dc3545' : undefined
                                }}
                              />
                              {seasonFormErrors.endDate && (
                                <Form.Control.Feedback type="invalid">
                                  {seasonFormErrors.endDate}
                                </Form.Control.Feedback>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-4">
                              <label className="form-label" style={{
                                fontSize: '14px',
                                color: '#6c757d',
                                fontWeight: '500',
                                marginBottom: '8px'
                              }}>
                                Location <span className="text-danger">*</span>
                              </label>
                              <Form.Control
                                type="text"
                                value={seasonFormData.location}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (SEASON_LOCATION_INPUT_PATTERN.test(value)) {
                                    setSeasonFormData({ ...seasonFormData, location: value });
                                    if (seasonFormErrors.location) {
                                      setSeasonFormErrors({ ...seasonFormErrors, location: '' });
                                    }
                                  }
                                }}
                                isInvalid={!!seasonFormErrors.location}
                                placeholder="Enter location"
                                style={{
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  color: '#212529',
                                  borderColor: seasonFormErrors.location ? '#dc3545' : undefined
                                }}
                              />
                              {seasonFormErrors.location && (
                                <Form.Control.Feedback type="invalid">
                                  {seasonFormErrors.location}
                                </Form.Control.Feedback>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-4">
                              <label className="form-label" style={{
                                fontSize: '14px',
                                color: '#6c757d',
                                fontWeight: '500',
                                marginBottom: '8px'
                              }}>
                                Season Year Range <span className="text-danger">*</span>
                              </label>
                              <Form.Control
                                type="text"
                                value={seasonFormData.seasonYearRange}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const yearRangePattern = /^[\d\-]*$/;
                                  if (yearRangePattern.test(value) || value === '') {
                                    setSeasonFormData({ ...seasonFormData, seasonYearRange: value });
                                    if (seasonFormErrors.seasonYearRange) {
                                      setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: '' });
                                    }
                                  }
                                }}
                                onBlur={() => {
                                  // Validate on blur
                                  const trimmedRange = seasonFormData.seasonYearRange.trim();
                                  if (trimmedRange) {
                                    const yearRangePattern = /^(\d{4})(-\d{4})?$/;
                                    if (!yearRangePattern.test(trimmedRange)) {
                                      setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: 'Season year range must be in format "2025" or "2025-2026"' });
                                    } else if (trimmedRange.includes('-')) {
                                      const [startYear, endYear] = trimmedRange.split('-').map(Number);
                                      if (endYear < startYear) {
                                        setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: 'End year must be greater than or equal to start year' });
                                      }
                                    }
                                  }
                                }}
                                isInvalid={!!seasonFormErrors.seasonYearRange}
                                placeholder="e.g., 2025 or 2025-2026"
                                style={{
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  color: '#212529',
                                  borderColor: seasonFormErrors.seasonYearRange ? '#dc3545' : undefined
                                }}
                              />
                              {seasonFormErrors.seasonYearRange && (
                                <Form.Control.Feedback type="invalid">
                                  {seasonFormErrors.seasonYearRange}
                                </Form.Control.Feedback>
                              )}
                              <small className="text-muted">
                                Format: YYYY (e.g., 2025) or YYYY-YYYY (e.g., 2025-2026)
                              </small>
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleCancelSeasonEdit}
                            disabled={seasonFormLoading}
                            style={{ borderRadius: '6px' }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            type="submit"
                            disabled={seasonFormLoading}
                            style={{ borderRadius: '6px' }}
                          >
                            {seasonFormLoading ? 'Saving...' : (editingSeason ? 'Update Season' : 'Add Season')}
                          </Button>
                        </div>
                      </Form>
                    ) : (
                      /* Display Mode */
                      <>
                        {seasonsLoading ? (
                          <ShimmerLoader type="table" />
                        ) : seasons.length === 0 ? (
                          /* Only show message if tournament doesn't have DEFAULT season */
                          !hasDefaultSeason && (
                            <div className="text-center py-4">
                              <p className="text-muted mb-0">No seasons added yet. Click "Add New Season" to create one.</p>
                            </div>
                          )
                        ) : (
                          <div>
                            {seasons.map((season) => {
                              const isExpanded = expandedSeasons.has(season._id);
                              const activeTab = seasonActiveTabs[season._id] || 'age-categories';

                              return (
                                <Card key={season._id} className="mb-3">
                                  <Card.Header
                                    className="d-flex justify-content-between align-items-center"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => toggleSeasonExpansion(season._id)}
                                  >
                                    <div className="d-flex align-items-center" style={{ flex: 1 }}>
                                      <i
                                        className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line me-2`}
                                        style={{ fontSize: '18px', color: '#6c757d' }}
                                      ></i>
                                      <div style={{ flex: 1 }}>
                                        <h5 className="mb-0 card-title">{season.seasonLabel || 'Unnamed Season'}</h5>
                                        <small className="text-muted">
                                          {season.startDate ? formatDate(season.startDate) : '-'} - {season.endDate ? formatDate(season.endDate) : '-'}
                                          {season.location && ` • ${season.location}`}
                                          {season.seasonYearRange && ` • ${season.seasonYearRange}`}
                                        </small>
                                      </div>
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <button
                                        className="action-btn btn-edit me-2"
                                        onClick={() => handleEditSeasonClick(season)}
                                        title="Edit Season"
                                      >
                                        <i className="ri-pencil-line"></i>
                                      </button>
                                      <button
                                        className="action-btn btn-delete"
                                        onClick={() => handleDeleteSeason(season)}
                                        disabled={!canDeleteSeason(season)}
                                        title={canDeleteSeason(season) ? "Delete Season" : "Cannot delete ongoing or completed seasons"}
                                        style={{
                                          opacity: canDeleteSeason(season) ? 1 : 0.5,
                                          cursor: canDeleteSeason(season) ? 'pointer' : 'not-allowed'
                                        }}
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </div>
                                  </Card.Header>
                                  {isExpanded && (
                                    <Card.Body>
                                      {/* Season Details Section - Collapsible */}
                                      <Card className="mb-3">
                                        <Card.Header
                                          className="d-flex justify-content-between align-items-center"
                                          style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                          onClick={() => {
                                            // Toggle season details expansion (can be tracked separately if needed)
                                          }}
                                        >
                                          <div className="d-flex align-items-center">
                                            <i className="ri-information-line me-2"></i>
                                            <h6 className="mb-0">Season Details</h6>
                                          </div>
                                        </Card.Header>
                                        <Card.Body>
                                          <Row>
                                            <Col md={6}>
                                              <div className="mb-3">
                                                <label className="text-muted" style={{ fontSize: '12px' }}>Season Name</label>
                                                <p className="mb-0">{season.seasonLabel || '-'}</p>
                                              </div>
                                            </Col>
                                            <Col md={6}>
                                              <div className="mb-3">
                                                <label className="text-muted" style={{ fontSize: '12px' }}>Season Year Range</label>
                                                <p className="mb-0">{season.seasonYearRange || '-'}</p>
                                              </div>
                                            </Col>
                                            <Col md={6}>
                                              <div className="mb-3">
                                                <label className="text-muted" style={{ fontSize: '12px' }}>Start Date</label>
                                                <p className="mb-0">{season.startDate ? formatDate(season.startDate) : '-'}</p>
                                              </div>
                                            </Col>
                                            <Col md={6}>
                                              <div className="mb-3">
                                                <label className="text-muted" style={{ fontSize: '12px' }}>End Date</label>
                                                <p className="mb-0">{season.endDate ? formatDate(season.endDate) : '-'}</p>
                                              </div>
                                            </Col>
                                            <Col md={6}>
                                              <div className="mb-3">
                                                <label className="text-muted" style={{ fontSize: '12px' }}>Location</label>
                                                <p className="mb-0">{season.location || '-'}</p>
                                              </div>
                                            </Col>
                                          </Row>
                                        </Card.Body>
                                      </Card>

                                      {/* Season Flow Tabs */}
                                      <Nav variant="tabs" className="mb-3">
                                        <Nav.Item>
                                          <Nav.Link
                                            active={activeTab === 'age-categories'}
                                            onClick={() => setSeasonActiveTab(season._id, 'age-categories')}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            Age Categories
                                          </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                          <Nav.Link
                                            active={activeTab === 'teams'}
                                            onClick={() => setSeasonActiveTab(season._id, 'teams')}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            Teams
                                          </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                          <Nav.Link
                                            active={activeTab === 'players'}
                                            onClick={() => setSeasonActiveTab(season._id, 'players')}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            Players
                                          </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                          <Nav.Link
                                            active={activeTab === 'standings'}
                                            onClick={() => setSeasonActiveTab(season._id, 'standings')}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            Standings
                                          </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                          <Nav.Link
                                            active={activeTab === 'match-management'}
                                            onClick={() => setSeasonActiveTab(season._id, 'match-management')}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            Fixtures
                                          </Nav.Link>
                                        </Nav.Item>
                                        {!isBadminton() && (
                                          <Nav.Item>
                                            <Nav.Link
                                              active={activeTab === 'player-stats'}
                                              onClick={() => setSeasonActiveTab(season._id, 'player-stats')}
                                              style={{ cursor: 'pointer' }}
                                            >
                                              Player Stats
                                            </Nav.Link>
                                          </Nav.Item>
                                        )}
                                      </Nav>

                                      {/* Tab Content */}
                                      <div style={{ minHeight: '200px' }}>
                                        {activeTab === 'age-categories' && (
                                          <AgeCategoryManagement
                                            ageCategories={ageCategories}
                                            search={ageCategorySearch}
                                            onSearch={setAgeCategorySearch}
                                            onRefresh={refreshAgeCategories}
                                            tournamentId={tournament._id}
                                            tournamentName={tournament.name}
                                            tournamentSports={tournament.sports}
                                            tournamentSeasonId={season._id}
                                            ageCategoriesLoading={ageCategoriesLoading}
                                            page={ageCategoryPage}
                                            pageSize={ageCategoryPageSize}
                                            totalPages={ageCategoryTotalPages}
                                            totalCount={ageCategoryTotalCount}
                                            onPageChange={setAgeCategoryPage}
                                            onPageSizeChange={size => { setAgeCategoryPageSize(size); setAgeCategoryPage(1); }}
                                            typeFilter={typeFilter}
                                            setTypeFilter={setTypeFilter}
                                            formatFilter={formatFilter}
                                            setFormatFilter={setFormatFilter}
                                            typeOptions={typeOptions}
                                            formatOptions={formatOptions}
                                            isStandingsContext={false}
                                          />
                                        )}
                                        {activeTab === 'teams' && (
                                          <TeamsTable
                                            teams={teams}
                                            search={teamSearch}
                                            onSearch={setTeamSearch}
                                            onEdit={handleEditTeam}
                                            onDelete={handleDeleteTeam}
                                            onAdd={handleAddTeam}
                                            ageCategories={ageCategories}
                                            tournamentId={tournament._id}
                                            tournamentSeasonId={season._id}
                                            seasonNumber={season.seasonNumber} // Pass season number for conditional "Add Existing Team" button
                                            teamsLoading={teamsLoading}
                                            page={teamsPage}
                                            pageSize={teamsPageSize}
                                            totalPages={teamsTotalPages}
                                            totalCount={teamsTotalCount}
                                            onPageChange={setTeamsPage}
                                            onPageSizeChange={size => { setTeamsPageSize(size); setTeamsPage(1); }}
                                            ageCategoryFilter={teamsAgeCategoryFilter}
                                            onAgeCategoryFilterChange={setTeamsAgeCategoryFilter}
                                            tournamentSports={tournament.sports}
                                          />
                                        )}
                                        {activeTab === 'players' && (
                                          <PlayersTable
                                            players={players}
                                            search={playerSearch}
                                            onSearch={setPlayerSearch}
                                            ageCategoryFilter={playersAgeCategoryFilter}
                                            onAgeCategoryFilterChange={setPlayersAgeCategoryFilter}
                                            onAdd={handleAddPlayer}
                                            onEdit={handleEditPlayer}
                                            onDelete={handleDeletePlayer}
                                            teams={teamOptions}
                                            ageCategories={ageCategoryOptionsForPlayers}
                                            loading={playersLoading}
                                            page={playersPage}
                                            pageSize={playersPageSize}
                                            totalPages={playersTotalPages}
                                            totalCount={playersTotalCount}
                                            onPageChange={setPlayersPage}
                                            onPageSizeChange={size => { setPlayersPageSize(size); setPlayersPage(1); }}
                                            tournamentId={tournament._id}
                                            tournamentSeasonId={season._id}
                                            tournamentSports={tournament?.sports || getSport()}
                                            isPlayerStats={false}
                                            onRefresh={() => setPlayersRefresh(v => !v)}
                                          />
                                        )}
                                        {activeTab === 'standings' && (
                                          <div>
                                            {standingsError ? (
                                              <div className="alert alert-danger">
                                                {standingsError}
                                              </div>
                                            ) : (
                                              <AgeCategoryManagement
                                                ageCategories={standings}
                                                search={standingsSearch}
                                                onSearch={setStandingsSearch}
                                                onRefresh={() => setStandingsRefresh(v => !v)}
                                                tournamentId={tournament._id}
                                                tournamentName={tournament.name}
                                                tournamentSports={tournament.sports}
                                                tournamentSeasonId={season._id}
                                                ageCategoriesLoading={standingsLoading}
                                                page={standingsPage}
                                                pageSize={standingsPageSize}
                                                totalPages={standingsTotalPages}
                                                totalCount={standingsTotalCount}
                                                onPageChange={setStandingsPage}
                                                onPageSizeChange={size => { setStandingsPageSize(size); setStandingsPage(1); }}
                                                typeFilter={typeFilter}
                                                setTypeFilter={setTypeFilter}
                                                formatFilter={formatFilter}
                                                setFormatFilter={setFormatFilter}
                                                typeOptions={typeOptions}
                                                formatOptions={formatOptions}
                                                isStandingsContext={true}
                                              />
                                            )}
                                          </div>
                                        )}
                                        {activeTab === 'match-management' && (
                                          <FixturesTable
                                            fixtures={fixtures}
                                            search={fixtureSearch}
                                            onSearch={setFixtureSearch}
                                            onAdd={handleAddFixture}
                                            onEdit={handleEditFixture}
                                            onDelete={handleDeleteFixture}
                                            ageCategories={ageCategories}
                                            tournamentId={tournament._id}
                                            tournamentSeasonId={season._id}
                                            fixturesLoading={fixturesLoading}
                                            page={fixturesPage}
                                            pageSize={fixturesPageSize}
                                            totalPages={fixturesTotalPages}
                                            totalCount={fixturesTotalCount}
                                            onPageChange={setFixturesPage}
                                            onPageSizeChange={size => { setFixturesPageSize(size); setFixturesPage(1); }}
                                            ageCategoryFilter={fixturesAgeCategoryFilter}
                                            onAgeCategoryFilterChange={setFixturesAgeCategoryFilter}
                                            statusFilter={matchStatusFilter}
                                            tournamentSports={tournament.sports}
                                            onStatusFilterChange={setMatchStatusFilter}
                                            onDownloadExcel={handleDownloadExcel}
                                            isDownloadingExcel={isDownloadingExcel}
                                          />
                                        )}
                                        {activeTab === 'player-stats' && !isBadminton() && (
                                          <PlayersTable
                                            players={playerStats}
                                            search={playerStatsSearch}
                                            onSearch={setPlayerStatsSearch}
                                            ageCategoryFilter={playerStatsAgeCategoryFilter}
                                            onAgeCategoryFilterChange={setPlayerStatsAgeCategoryFilter}
                                            onAdd={handleAddPlayer}
                                            onEdit={handleEditPlayer}
                                            onDelete={handleDeletePlayer}
                                            teams={teamOptions}
                                            ageCategories={ageCategoryOptionsForPlayers}
                                            loading={playerStatsLoading}
                                            page={playerStatsPage}
                                            pageSize={playerStatsPageSize}
                                            totalPages={playerStatsTotalPages}
                                            totalCount={playerStatsTotalCount}
                                            onPageChange={setPlayerStatsPage}
                                            onPageSizeChange={size => { setPlayerStatsPageSize(size); setPlayersPage(1); }}
                                            tournamentId={tournament._id}
                                            tournamentSeasonId={season._id}
                                            tournamentSports={tournament?.sports || getSport()}
                                            isPlayerStats={true}
                                            onOpenPlayerStatsDetail={(playerId: string) => navigate(`/manage-tournaments-fixtures/${tournament._id}/seasons/${season._id}/player-stats/${playerId}?sport=${encodeURIComponent(getSport())}`, {
                                              state: {
                                                tournamentSeasonId: season._id,
                                                fromTournamentDetailPage: true,
                                                selectedTournament: tournament // Include tournament so sport can be extracted
                                              }
                                            })}
                                            onRefresh={() => setPlayerStatsRefresh(v => !v)}
                                          />
                                        )}
                                      </div>
                                    </Card.Body>
                                  )}
                                </Card>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              )}
            </div>
          ) : activeTab === 'age-categories' ? (
            /* Age Categories Tab */
            <div style={{ padding: '0 15px' }}>
              <AgeCategoryManagement
                ageCategories={ageCategories}
                search={ageCategorySearch}
                onSearch={setAgeCategorySearch}
                onRefresh={refreshAgeCategories}
                tournamentId={tournament._id}
                tournamentName={tournament.name}
                tournamentSports={tournament.sports}
                tournamentSeasonId={defaultSeasonId} // Pass defaultSeasonId for single-season tournaments
                ageCategoriesLoading={ageCategoriesLoading}
                page={ageCategoryPage}
                pageSize={ageCategoryPageSize}
                totalPages={ageCategoryTotalPages}
                totalCount={ageCategoryTotalCount}
                onPageChange={setAgeCategoryPage}
                onPageSizeChange={size => { setAgeCategoryPageSize(size); setAgeCategoryPage(1); }}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                formatFilter={formatFilter}
                setFormatFilter={setFormatFilter}
                typeOptions={typeOptions}
                formatOptions={formatOptions}
                isStandingsContext={false}
              />
            </div>
          ) : activeTab === 'teams' ? (
            /* Teams Tab */
            <div style={{ padding: '0 15px' }}>
              <TeamsTable
                teams={teams}
                search={teamSearch}
                onSearch={setTeamSearch}
                onEdit={handleEditTeam}
                onDelete={handleDeleteTeam}
                onAdd={handleAddTeam}
                ageCategories={ageCategories}
                tournamentId={tournament._id}
                tournamentSeasonId={defaultSeasonId} // Pass defaultSeasonId for single-season tournaments
                teamsLoading={teamsLoading}
                page={teamsPage}
                pageSize={teamsPageSize}
                totalPages={teamsTotalPages}
                totalCount={teamsTotalCount}
                onPageChange={setTeamsPage}
                onPageSizeChange={size => { setTeamsPageSize(size); setTeamsPage(1); }}
                ageCategoryFilter={teamsAgeCategoryFilter}
                onAgeCategoryFilterChange={setTeamsAgeCategoryFilter}
                tournamentSports={tournament.sports}
              />
            </div>
          ) : activeTab === 'players' ? (
            /* Players Tab */
            <div style={{ padding: '0 15px' }}>
              <PlayersTable
                players={players}
                search={playerSearch}
                onSearch={setPlayerSearch}
                ageCategoryFilter={playersAgeCategoryFilter}
                onAgeCategoryFilterChange={setPlayersAgeCategoryFilter}
                onAdd={handleAddPlayer}
                onEdit={handleEditPlayer}
                onDelete={handleDeletePlayer}
                teams={teamOptions}
                ageCategories={ageCategoryOptionsForPlayers}
                loading={playersLoading}
                page={playersPage}
                pageSize={playersPageSize}
                totalPages={playersTotalPages}
                totalCount={playersTotalCount}
                onPageChange={setPlayersPage}
                onPageSizeChange={size => { setPlayersPageSize(size); setPlayersPage(1); }}
                tournamentId={tournament._id}
                tournamentSeasonId={defaultSeasonId} // Pass defaultSeasonId for single-season tournaments
                tournamentSports={tournament.sports}
              />
            </div>
          ) : activeTab === 'standings' ? (
            /* Standings Tab - Now showing Age Categories with standingsType */
            <div style={{ padding: '0 15px' }}>
              {standingsError ? (
                <div className="alert alert-danger">{standingsError}</div>
              ) : (
                <AgeCategoryManagement
                  ageCategories={standings}
                  search={standingsSearch}
                  onSearch={setStandingsSearch}
                  onRefresh={() => setStandingsRefresh(v => !v)}
                  tournamentId={tournament._id}
                  tournamentName={tournament?.name || ''}
                  tournamentSports={tournament?.sports || ''}
                  tournamentSeasonId={defaultSeasonId} // Pass defaultSeasonId for single-season tournaments
                  ageCategoriesLoading={standingsLoading}
                  page={standingsPage}
                  pageSize={standingsPageSize}
                  totalPages={standingsTotalPages}
                  totalCount={standingsTotalCount}
                  onPageChange={setStandingsPage}
                  onPageSizeChange={size => { setStandingsPageSize(size); setStandingsPage(1); }}
                  typeFilter={typeFilter}
                  setTypeFilter={setTypeFilter}
                  formatFilter={formatFilter}
                  setFormatFilter={setFormatFilter}
                  typeOptions={typeOptions}
                  formatOptions={formatOptions}
                  isStandingsContext={true}
                />
              )}
            </div>
          ) : activeTab === 'match-management' ? (
            <div style={{ padding: '0 15px' }}>
              <FixturesTable
                fixtures={fixtures}
                search={fixtureSearch}
                onSearch={setFixtureSearch}
                ageCategoryFilter={fixturesAgeCategoryFilter}
                onAgeCategoryFilterChange={setFixturesAgeCategoryFilter}
                statusFilter={matchStatusFilter}
                onStatusFilterChange={(filter) => { setMatchStatusFilter(filter); setFixturesPage(1); }}
                onAdd={handleAddFixture}
                onEdit={handleEditFixture}
                onDelete={handleDeleteFixture}
                ageCategories={ageCategoryOptionsForPlayers}
                fixturesLoading={fixturesLoading}
                page={fixturesPage}
                pageSize={fixturesPageSize}
                totalPages={fixturesTotalPages}
                totalCount={fixturesTotalCount}
                onPageChange={setFixturesPage}
                onPageSizeChange={size => { setFixturesPageSize(size); setFixturesPage(1); }}
                tournamentId={tournament._id}
                tournamentSports={tournament.sports}
                tournamentSeasonId={defaultSeasonId} // Pass defaultSeasonId for single-season tournaments
                initialOpenFixture={initialOpenFixture}
                navigateToDetailPage={true}
                onDownloadExcel={handleDownloadExcel}
                isDownloadingExcel={isDownloadingExcel}
              />
            </div>
          ) : activeTab === 'player-stats' && !isBadminton() ? (
            <div style={{ padding: '0 15px' }}>
              <PlayersTable
                players={playerStats}
                search={playerStatsSearch}
                onSearch={setPlayerStatsSearch}
                ageCategoryFilter={playerStatsAgeCategoryFilter}
                onAgeCategoryFilterChange={setPlayerStatsAgeCategoryFilter}
                onAdd={handleAddPlayer}
                onEdit={handleEditPlayer}
                onDelete={handleDeletePlayer}
                teams={teamOptions}
                ageCategories={ageCategoryOptionsForPlayers}
                loading={playerStatsLoading}
                page={playerStatsPage}
                pageSize={playerStatsPageSize}
                totalPages={playerStatsTotalPages}
                totalCount={playerStatsTotalCount}
                onPageChange={setPlayerStatsPage}
                onPageSizeChange={size => { setPlayerStatsPageSize(size); setPlayerStatsPage(1); }}
                tournamentId={tournament._id}
                tournamentSeasonId={defaultSeasonId} // Pass defaultSeasonId for single-season tournaments
                tournamentSports={tournament?.sports || getSport()}
                isPlayerStats={true}
                onOpenPlayerStatsDetail={(playerId: string) => navigate(`/manage-tournaments-fixtures/${tournament._id}/seasons/${defaultSeasonId}/player-stats/${playerId}?sport=${encodeURIComponent(getSport())}`, {
                  state: {
                    tournamentSeasonId: defaultSeasonId,
                    fromTournamentDetailPage: true,
                    selectedTournament: tournament // Include tournament so sport can be extracted
                  }
                })}
                onRefresh={() => setPlayerStatsRefresh(v => !v)}
              />
            </div>
          ) : activeTab === 'settings' ? (
            <Card>
              <Card.Header><h5 className="mb-0">Season features</h5></Card.Header>
              <Card.Body>
                {featureToggleError && <div className="alert alert-warning">{featureToggleError}</div>}
                {!defaultSeasonId ? (
                  <span className="text-muted">The default tournament season is still loading.</span>
                ) : featureToggles.length ? featureToggles.map((toggle) => (
                  <Form.Check
                    className="mb-3"
                    key={toggle.name}
                    type="switch"
                    id={`feature-${toggle.name}`}
                    label={toggle.label}
                    checked={toggle.enabled}
                    onChange={(event) => updateFeatureToggle(toggle, event.target.checked)}
                    disabled={!canManageContent || savingFeatureToggles.includes(toggle.name)}
                  />
                )) : featureTogglesLoading ? (
                  <span className="text-muted">Loading season settings…</span>
                ) : (
                  <span className="text-muted">No season features are available.</span>
                )}
              </Card.Body>
            </Card>
          ) : activeTab === 'videos' ? (
            defaultSeasonId ? (
              <VideosTab
                tournamentId={tournament._id}
                seasonId={defaultSeasonId}
                returnPath={`/manage-tournaments-fixtures/${tournament._id}`}
                parentState={{ selectedTournament: tournament, defaultSeasonId }}
              />
            ) : (
              <div className="text-center py-5 text-muted">The default tournament season is still loading.</div>
            )
          ) : activeTab === 'content' ? (
            <TournamentContentTab
              tournamentId={tournament._id}
              sport={getSport()}
              canManage={canManageContent}
            />
          ) : activeTab === 'voting' ? (
            <VotingManagement
              tournamentId={tournament._id}
              seasonId={defaultSeasonId}
              tournamentSports={tournament?.sports || getSport()}
              ageCategoryOptionsList={ageCategoryOptionsForPlayers}
            />
          ) : activeTab === 'predictions' ? (
            <PredictionsManagement
              tournamentId={tournament._id}
              seasonId={defaultSeasonId}
              tournamentSports={tournament?.sports || getSport()}
            />
          ) : null}
        </div>
      </Container >

      {/* Edit Tournament Modal */}
      < Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" >
        <Modal.Header closeButton>
          <Modal.Title>Edit Tournament</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tournament Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Short Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.shortName}
                    onChange={(e) => setEditFormData({ ...editFormData, shortName: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sports</Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.sports}
                    onChange={(e) => setEditFormData({ ...editFormData, sports: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={editLoading}>
              {editLoading ? 'Updating...' : 'Update Tournament'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal >

      {/* Download Excel Modal */}
      < Modal
        show={showDownloadExcelModal}
        onHide={handleCloseDownloadExcelModal}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Download Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {excelDownloadError && (
            <div className="alert alert-danger mb-3" role="alert">
              {excelDownloadError}
            </div>
          )}
          <Form>
            <Row className="g-3">
              <Col md={8}>
                <Form.Label className="mb-2 d-block">
                  Select Date Range <span className="text-danger">*</span>
                  <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                    Select a single date or a date range. Dates with matches are marked with a green dot.
                  </small>
                </Form.Label>
                {loadingCalendarEvents ? (
                  <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading calendar events...
                  </div>
                ) : (
                  <MatchCalendar
                    calendarEvents={calendarEvents}
                    startDate={excelDownloadStartDate}
                    endDate={excelDownloadEndDate}
                    onStartDateChange={(date) => {
                      setExcelDownloadStartDate(date);
                      setExcelDownloadError(null);
                    }}
                    onEndDateChange={(date) => {
                      setExcelDownloadEndDate(date);
                      setExcelDownloadError(null);
                    }}
                    disabled={isDownloadingExcel}
                  />
                )}
                {!excelDownloadStartDate && (
                  <div className="text-danger small mt-2">
                    Please select at least a start date
                  </div>
                )}
              </Col>
              <Col md={4}>
                <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={excelDownloadStatus ? { value: excelDownloadStatus, label: excelDownloadStatus === 'pending' ? 'Upcoming' : 'Completed' } : null}
                  onChange={(option: any) => {
                    setExcelDownloadStatus(option?.value || '');
                    setExcelDownloadError(null);
                  }}
                  options={[
                    { value: 'pending', label: 'Upcoming' },
                    { value: 'completed', label: 'Completed' }
                  ]}
                  placeholder="Select Status"
                  isClearable={false}
                  isDisabled={isDownloadingExcel}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '38px',
                      border: excelDownloadError && !excelDownloadStatus ? '1px solid #dc3545' : '1px solid #ced4da',
                      borderRadius: '6px'
                    })
                  }}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDownloadExcelModal} disabled={isDownloadingExcel}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleExcelDownloadSubmit}
            disabled={isDownloadingExcel || !excelDownloadStartDate || !excelDownloadStatus}
          >
            {isDownloadingExcel ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Downloading...
              </>
            ) : (
              <>
                <i className="ri-file-excel-line me-2"></i>
                Download Excel
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal >
    </div >
  );
};

export default TournamentDetailPage;
